/* Activity: constellation view for Action Center runs. */
(function () {
  const { useCallback, useEffect, useMemo, useRef, useState } = React;

  const CUSTOM_INSTRUCTION_ID = "__custom__";
  const BRIDGE_ACTIVITY_PULSE_MS = 12000;
  const DELEGATION_ANIMATION_MS = 2600;
  const TOOL_ACTIVITY_VISIBLE_MS = 3000;
  const TOOL_ACTIVITY_FADE_MS = 520;
  const TOOL_ACTIVITY_STACK_MAX = 4;
  const ACTION_CENTER_BRIDGE_ACTIVITY_EVENT = "action-center-bridge-activity";
  const STATUS_META = {
    queued: { label: "Queued", cls: "queued", color: "var(--muted)", soft: "#eef2f7" },
    working: { label: "Working", cls: "working", color: "var(--primary)", soft: "var(--primary-soft)" },
    "waiting-delegates": { label: "Working", cls: "working", color: "var(--primary)", soft: "var(--primary-soft)" },
    "needs-input": { label: "Needs you", cls: "needs", color: "var(--warn)", soft: "var(--warn-soft)" },
    review: { label: "Finished", cls: "review", color: "var(--info)", soft: "#e2f0f3" },
    done: { label: "Done", cls: "done", color: "var(--pos)", soft: "var(--pos-soft)" },
    error: { label: "Blocked", cls: "error", color: "var(--neg)", soft: "var(--neg-soft)" },
  };
  const SOURCE_META = {
    task: { label: "Task", icon: "check" },
    ticket: { label: "Ticket", icon: "tag" },
    email: { label: "Reply", icon: "mail" },
    "project-email": { label: "Reply", icon: "mail" },
    "email-message": { label: "Reply", icon: "mail" },
    followup: { label: "Follow-up", icon: "calendar" },
    "meeting-followup": { label: "Follow-up", icon: "calendar" },
    file: { label: "File", icon: "doc" },
    handoff: { label: "Handoff", icon: "handover" },
    "delegated-task": { label: "Handoff", icon: "handover" },
  };
  const PRIORITY_LABEL = { high: "High", med: "Med", medium: "Med", low: "Low", normal: "Med" };

  function norm(value) { return String(value || "").trim().toLowerCase(); }
  function sameId(a, b) { return String(a || "").toLowerCase() === String(b || "").toLowerCase(); }
  function firstName(name) { return String(name || "Agent").trim().split(/\s+/)[0] || "Agent"; }
  function rosterSearchText(agent) {
    return [
      agent && agent.name,
      agent && agent.role,
      agent && agent.description,
      ...((agent && agent.skillNames) || []),
      ...((agent && agent.pluginNames) || []),
    ].join(" ").toLowerCase();
  }
  function dateTimeLabel(value) {
    if (!value) return "";
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? "" : date.toLocaleString([], { month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" });
  }
  function statusOf(run) { return norm(run && run.status) || "queued"; }
  function userStopped(run) { return statusOf(run) === "error" && String(run && (run.error || run.Error) || "").trim().toLowerCase().startsWith("stopped by user"); }
  function statusMeta(status) { return STATUS_META[norm(status)] || STATUS_META.queued; }
  function runStatusIcon(run) {
    const status = statusOf(run);
    if (status === "working" || status === "waiting-delegates") return "tool";
    if (status === "queued") return "clock";
    if (status === "needs-input" || status === "error") return "alert";
    if (status === "review") return "bookOpen";
    if (status === "done") return "check";
    return "";
  }
  function sourceKey(item) { return norm((item && (item.source || item.sourceType || item.type)) || "task"); }
  function sourceMeta(value) { return SOURCE_META[value] || SOURCE_META[norm(value)] || SOURCE_META.task; }
  function displayPct(run) { return Math.max(0, Math.min(100, Number(run && (run.percent ?? run.pct)) || 0)); }
  function priorityClass(priority) {
    const clean = norm(priority);
    if (clean === "high") return "high";
    if (clean === "low") return "low";
    return "med";
  }
  function priorityLabel(priority) { return PRIORITY_LABEL[norm(priority)] || priority || "Med"; }
  function streamPreview(value, maxLength = 120) {
    const text = String(value || "").replace(/\s+/g, " ").trim();
    if (text.length <= maxLength) return text;
    return `${text.slice(0, maxLength).trimEnd()}...`;
  }
  function bubbleTitleLabel(run) {
    return String(run && (run.threadTitle || run.title || run.contextScopeLabel) || "Thread").replace(/\s+/g, " ").trim();
  }
  function planStatusClass(status) {
    const clean = norm(status);
    if (clean === "active" || clean === "pending") return clean;
    if (clean === "blocked") return "blocked";
    if (clean === "completed" || clean === "done") return "done";
    if (clean === "inprogress" || clean === "in_progress" || clean === "running") return "active";
    if (clean === "failed" || clean === "error" || clean === "errored") return "blocked";
    return "pending";
  }
  function planStepsFromActivity(activity) {
    const plan = activity && Array.isArray(activity.plan) ? activity.plan : [];
    return plan.map((step) => ({
      step: step && (step.step || step.Step) || "",
      status: planStatusClass(step && (step.status || step.Status)),
    })).filter((step) => step.step);
  }
  function runSort(a, b) {
    const rank = { "needs-input": 0, error: 1, working: 2, "waiting-delegates": 2, queued: 3, review: 4, done: 5 };
    const ar = rank[statusOf(a)] ?? 9;
    const br = rank[statusOf(b)] ?? 9;
    if (ar !== br) return ar - br;
    const createdDiff = new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
    return createdDiff || String(a.id || "").localeCompare(String(b.id || ""));
  }
  function stableJson(value) { return JSON.stringify(value || null); }
  function sameRecord(a, b) { return stableJson(a) === stableJson(b); }
  function reconcileList(previous, next, keyFn) {
    const oldByKey = {};
    (previous || []).forEach((item) => { oldByKey[keyFn(item)] = item; });
    let changed = (previous || []).length !== (next || []).length;
    const reconciled = (next || []).map((item, index) => {
      const key = keyFn(item);
      const old = oldByKey[key];
      if (old && sameRecord(old, item)) {
        if ((previous || [])[index] !== old) changed = true;
        return old;
      }
      changed = true;
      return item;
    });
    return changed ? reconciled : (previous || []);
  }
  function reconcileDashboard(previous, next) {
    const reconciled = {
      agents: reconcileList(previous.agents, next.agents || [], (item) => item.id),
      backlog: reconcileList(previous.backlog, next.backlog || [], (item) => item.id),
      runs: reconcileList(previous.runs, next.runs || [], (item) => item.id),
      taggedTickets: reconcileTaggedTickets(previous.taggedTickets, next.taggedTickets || []),
      instructions: reconcileList(previous.instructions, next.instructions || [], (item) => item.id),
      needsInputCount: Number(next.needsInputCount) || 0,
    };
    return reconciled.agents === previous.agents
      && reconciled.backlog === previous.backlog
      && reconciled.runs === previous.runs
      && reconciled.taggedTickets === previous.taggedTickets
      && reconciled.instructions === previous.instructions
      && reconciled.needsInputCount === previous.needsInputCount
      ? previous
      : reconciled;
  }
  function reconcileTaggedTickets(previous, next) {
    return reconcileList(previous, next || [], (item) => `${item.projectId || ""}:${item.ticketId || ""}`);
  }
  function reconcileRunUpdate(previous, runId, update) {
    const currentRuns = (previous.runs || []).filter((run) => run.id !== runId);
    const updateRun = update && update.run ? update.run : null;
    const status = statusOf(updateRun);
    const keepRun = updateRun && !updateRun.archived && (update.active || status === "done" || status === "error");
    const nextRuns = keepRun ? [...currentRuns, updateRun].sort(runSort) : currentRuns;
    return {
      ...previous,
      runs: reconcileList(previous.runs, nextRuns, (item) => item.id),
      needsInputCount: Number(update && update.needsInputCount) || 0,
    };
  }
  function isActionCenterEvent(change) {
    return norm(change && change.toolName).startsWith("action-center/");
  }
  function isDelegationEvent(change) {
    const kind = norm(change && change.toolName);
    return kind === "action-center/delegation" || kind === "action-center/delegation-return";
  }
  function delegationDirection(change) {
    return norm(change && change.toolName) === "action-center/delegation-return" ? "return" : "handoff";
  }
  function isDurableActionCenterEvent(change) {
    const message = norm(change && change.message);
    return isDelegationEvent(change)
      || message === "action center task queued."
      || message === "action center task claimed."
      || message === "action center task updated."
      || message === "action center input requested."
      || message === "action center input cleared."
      || message === "action center input answered."
      || message === "action center message sent."
      || message === "action center task approved."
      || message === "action center run deleted."
      || message === "action center thread archived."
      || message === "action center thread unarchived.";
  }
  function isWorkedTicketRefreshEvent(change) {
    if (norm(change && change.kind) !== "change") return false;
    const tool = norm(change && change.toolName);
    return tool === "project_ticket_get"
      || tool === "project_ticket_update"
      || tool === "project_ticket_delete"
      || tool === "project_ticket_move"
      || tool === "project_tickets_reorder";
  }
  function runParentId(run) {
    return run && (run.parentRunId || (norm(run.sourceType) === "delegated-task" ? run.parentId : ""));
  }
  function handoffLabel(run) {
    return (run && (run.handoffLabel || (norm(run.sourceType) === "delegated-task" ? run.title : ""))) || "Delegated task";
  }
  function employeeAvatar(employee, sizeClass = "") {
    const preset = window.avatarPreset ? window.avatarPreset(employee && employee.avatarKey) : null;
    const color = window.avatarColor ? window.avatarColor((employee && (employee.avatarKey || employee.name)) || "") : "#dce3ea";
    return (
      <span className={`av-avatar ${preset && preset.image ? "has-image" : ""} ${sizeClass}`} style={{ background: preset && preset.image ? "#dce3ea" : color }}>
        {preset && preset.image
          ? <img src={preset.image} alt="" aria-hidden="true" />
          : preset ? <span className="av-avatar-glyph" aria-hidden="true">{preset.glyph}</span> : window.initials(employee && employee.name)}
      </span>
    );
  }
  function employeeColor(employee) {
    return window.avatarColor ? window.avatarColor((employee && (employee.avatarKey || employee.name)) || "") : "#2856c4";
  }
  function isInteractiveTarget(target) {
    return !!(target && target.closest && target.closest("button, a, input, select, textarea"));
  }
  function currentUrlWithoutHash() {
    return `${window.location.origin}${window.location.pathname}${window.location.search}`;
  }
  function activityReferenceFor(employee) {
    return {
      type: "Activity",
      id: employee && employee.id ? employee.id : "",
      label: employee && employee.name ? `New thread with ${employee.name}` : "New Activity thread",
      url: currentUrlWithoutHash(),
      fields: {
        Employee: (employee && employee.name) || "",
        EmployeeId: (employee && employee.id) || "",
        Role: (employee && employee.role) || "AI employee",
      },
    };
  }
  function submitPromptOnEnter(event, submit) {
    if (!event || event.key !== "Enter" || event.shiftKey || event.defaultPrevented || (event.nativeEvent && event.nativeEvent.isComposing)) return;
    event.preventDefault();
    if (submit) submit();
  }
  function approvalRequestFromRun(run) {
    if (window.actionCenterApprovalFromRun) return window.actionCenterApprovalFromRun(run);
    const requestId = String(run && (run.approvalRequestId || run.ApprovalRequestId) || "").trim();
    const response = String(run && (run.approvalResponseText || run.ApprovalResponseText || run.approvalResponse || run.ApprovalResponse) || "").trim();
    if (!requestId || response) return null;
    return {
      requestId,
      method: run.approvalRequestMethod || run.ApprovalRequestMethod || "",
      title: run.approvalRequestTitle || run.ApprovalRequestTitle || "Approval required",
      body: run.approvalRequestBody || run.ApprovalRequestBody || "",
      payloadJson: run.approvalRequestPayloadJson || run.ApprovalRequestPayloadJson || "",
      response,
    };
  }
  function approvalJobIdForRun(run) {
    if (window.actionCenterApprovalJobIdForRun) return window.actionCenterApprovalJobIdForRun(run);
    return norm(run && (run.sourceType || run.SourceType)) === "delegated-task" && (run.sourceId || run.SourceId)
      ? run.sourceId || run.SourceId
      : run && (run.id || run.Id);
  }
  function approvalRequestPreview(approvalRequest) {
    return String(approvalRequest && (approvalRequest.title || approvalRequest.body) || "Needs your input").trim();
  }
  function approvalToastForDecision(decision) {
    if (window.actionCenterApprovalToast) return window.actionCenterApprovalToast(decision);
    const clean = norm(decision);
    if (["accept", "allow", "approve", "approved"].includes(clean)) return "Approved";
    if (["abort", "cancel", "cancelled", "canceled"].includes(clean)) return "Cancelled";
    return "Declined";
  }
  function structuredQuestionDecision(value) {
    if (window.actionCenterStructuredQuestionDecision) return window.actionCenterStructuredQuestionDecision(value);
    try {
      const parsed = JSON.parse(String(value || ""));
      return Array.isArray(parsed && parsed.answers);
    } catch {
      return false;
    }
  }
  function finalAgentMessage(messages, run) {
    const list = Array.isArray(messages) ? messages : [];
    const latestAssistant = list.slice().reverse().find((message) => {
      const kind = norm(message && message.kind);
      const role = norm(message && message.role);
      return String(message && message.text || "").trim()
        && (role === "assistant" || kind === "agent" || kind === "delegation-continuation");
    });
    if (latestAssistant) return latestAssistant.text;
    const latestDone = list.slice().reverse().find((message) => norm(message && message.kind) === "done" && String(message.text || "").trim());
    if (latestDone) return latestDone.text;
    return (run && run.error) || "No final message was recorded for this run.";
  }
  async function copyMarkdownText(text) {
    const value = String(text || "");
    if (!value.trim()) return false;
    if (navigator.clipboard && window.ClipboardItem) {
      try {
        const item = new ClipboardItem({
          "text/markdown": new Blob([value], { type: "text/markdown" }),
          "text/plain": new Blob([value], { type: "text/plain" }),
        });
        await navigator.clipboard.write([item]);
        return true;
      } catch {
        // Fall through to writeText for browsers that do not accept text/markdown.
      }
    }
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(value);
      return true;
    }
    const textarea = document.createElement("textarea");
    textarea.value = value;
    textarea.setAttribute("readonly", "");
    textarea.style.position = "fixed";
    textarea.style.left = "-9999px";
    document.body.appendChild(textarea);
    textarea.select();
    const copied = document.execCommand("copy");
    document.body.removeChild(textarea);
    return copied;
  }

  function activityToolKey(activity) {
    return window.ActionCenterActivity ? window.ActionCenterActivity.activityToolKey(activity) : "";
  }
  function toolActionMode(toolKey) {
    return window.ActionCenterActivity ? window.ActionCenterActivity.toolActionMode(toolKey) : "";
  }
  function toolActionLabel(toolKey, mode) {
    return window.ActionCenterActivity ? window.ActionCenterActivity.toolActionLabel(toolKey, mode) : "Work";
  }
  function toolPathDomain(toolKey) {
    const orgKnowledge = toolKey.startsWith("organization_knowledge_") || toolKey.startsWith("organization_okf_") || toolKey.startsWith("organization_source_");
    const projectKnowledge = toolKey.startsWith("project_knowledge_") || toolKey.startsWith("project_okf_") || toolKey.startsWith("project_source_");
    if (orgKnowledge || projectKnowledge) {
      return {
        domainKey: "knowledge",
        root: orgKnowledge ? "Org OKF" : "OKF",
        section: toolKey.includes("context") ? "Context"
          : toolKey.includes("concept") ? "Concepts"
          : toolKey.includes("source") ? "Sources"
          : toolKey.includes("artifact") ? "Artifacts"
          : toolKey.includes("okf") ? "Bundle"
          : "Knowledge",
      };
    }
    if (toolKey.startsWith("project_work_package_") || toolKey.startsWith("project_work_packages_")) return { domainKey: "work", root: "Project", section: "Work packages" };
    if (toolKey.startsWith("project_ticket_cluster_") || toolKey.startsWith("project_ticket_clusters_")) return { domainKey: "tickets", root: "Project", section: "Ticket clusters" };
    if (toolKey.startsWith("project_ticket_") || toolKey.startsWith("project_tickets_")) return { domainKey: "tickets", root: "Project", section: "Tickets" };
    if (toolKey.startsWith("project_test_suite_")) return { domainKey: "tests", root: "Tests", section: "Suite" };
    if (toolKey.startsWith("project_test_case_")) return { domainKey: "tests", root: "Tests", section: "Case" };
    if (toolKey.startsWith("project_test_step_")) return { domainKey: "tests", root: "Tests", section: "Step" };
    if (toolKey.startsWith("project_test_environment_")) return { domainKey: "tests", root: "Tests", section: "Environment" };
    if (toolKey.startsWith("project_test_run_")) return { domainKey: "tests", root: "Tests", section: "Run" };
    if (toolKey.startsWith("project_test_")) return { domainKey: "tests", root: "Tests", section: "Dashboard" };
    if (toolKey.startsWith("email_draft_")) return { domainKey: "email", root: "Email", section: "Draft" };
    if (toolKey.startsWith("email_message_")) return { domainKey: "email", root: "Email", section: "Message" };
    if (toolKey.startsWith("email_project_")) return { domainKey: "email", root: "Email", section: "Project link" };
    if (toolKey.startsWith("document_project_")) return { domainKey: "documents", root: "Documents", section: "Project" };
    if (toolKey.startsWith("document_")) return { domainKey: "documents", root: "Documents", section: "Library" };
    if (toolKey.startsWith("meeting_transcript_")) return { domainKey: "meetings", root: "Meetings", section: "Transcript" };
    if (toolKey.startsWith("meeting_followup_")) return { domainKey: "meetings", root: "Meetings", section: "Follow-up" };
    if (toolKey.startsWith("meeting_")) return { domainKey: "meetings", root: "Meetings", section: "Schedule" };
    if (toolKey.startsWith("project_sketch_") || toolKey.startsWith("project_sketches_")) return { domainKey: "design", root: "Design", section: "Sketches" };
    if (toolKey.startsWith("project_tech_stack_")) return { domainKey: "architecture", root: "Architecture", section: "Tech stack" };
    if (toolKey.startsWith("project_design_guideline_") || toolKey.startsWith("project_work_package_design_guideline_")) return { domainKey: "guidelines", root: "Guidelines", section: "Design" };
    if (toolKey.startsWith("implementation_guideline_") || toolKey.startsWith("project_implementation_guideline_")) return { domainKey: "guidelines", root: "Guidelines", section: "Implementation" };
    if (toolKey.startsWith("invoice_")) return { domainKey: "finance", root: "Finance", section: "Invoice" };
    if (toolKey.startsWith("expense_")) return { domainKey: "finance", root: "Finance", section: "Expense" };
    if (toolKey === "cashflow_summary") return { domainKey: "finance", root: "Finance", section: "Cashflow" };
    if (toolKey.startsWith("project_milestone_") || toolKey.startsWith("project_milestones_")) return { domainKey: "delivery", root: "Delivery", section: "Milestones" };
    if (toolKey.startsWith("project_deliverable_")) return { domainKey: "delivery", root: "Delivery", section: "Deliverables" };
    if (toolKey.startsWith("project_offer_")) return { domainKey: "delivery", root: "Delivery", section: "Offers" };
    if (toolKey.startsWith("note_") || toolKey.startsWith("project_note_")) return { domainKey: "notes", root: "Notes", section: "Project" };
    if (toolKey.startsWith("project_customer_")) return { domainKey: "projects", root: "Projects", section: "Customers" };
    if (toolKey.startsWith("project_")) return { domainKey: "projects", root: "Projects", section: "Records" };
    if (toolKey.startsWith("customer_")) return { domainKey: "directory", root: "Directory", section: "Customers" };
    if (toolKey.startsWith("employee_")) return { domainKey: "directory", root: "Directory", section: "Employees" };
    if (toolKey.startsWith("contact_person_")) return { domainKey: "directory", root: "Directory", section: "Contacts" };
    if (toolKey.startsWith("ai_employee_task_")) return { domainKey: "delegation", root: "Delegation", section: "Tasks" };
    if (toolKey.startsWith("action_center_")) return { domainKey: "action", root: "Action Center", section: "Thread" };
    if (toolKey.startsWith("action_item_")) return { domainKey: "action", root: "Action Center", section: "Inbox" };
    return null;
  }
  function parseToolArguments(activity) {
    const raw = activity && (activity.toolArgumentsJson || activity.toolArguments || activity.argumentsJson || activity.arguments);
    if (!raw) return null;
    if (typeof raw === "object") return raw;
    try {
      return JSON.parse(String(raw));
    } catch {
      return null;
    }
  }
  function cleanArgumentLabel(value) {
    const text = String(value || "").trim();
    if (!text || text.length > 180) return "";
    return text.replace(/\s+/g, " ");
  }
  function argumentValue(args, keys) {
    if (!args || typeof args !== "object") return "";
    for (const key of keys) {
      if (Object.prototype.hasOwnProperty.call(args, key)) {
        const label = cleanArgumentLabel(args[key]);
        if (label) return label;
      }
    }
    for (const value of Object.values(args)) {
      if (!value || typeof value !== "object" || Array.isArray(value)) continue;
      const nested = argumentValue(value, keys);
      if (nested) return nested;
    }
    return "";
  }
  function recordDisplayName(record) {
    if (!record || typeof record !== "object") return "";
    return cleanArgumentLabel(record.name)
      || cleanArgumentLabel(record.title)
      || cleanArgumentLabel(record.fileName)
      || cleanArgumentLabel(record.filename)
      || cleanArgumentLabel(record.subject)
      || cleanArgumentLabel(record.invoiceNumber)
      || cleanArgumentLabel(record.number)
      || cleanArgumentLabel(record.email)
      || cleanArgumentLabel(record.path)
      || cleanArgumentLabel(record.category)
      || cleanArgumentLabel(record.description);
  }
  function resolveRecordName(db, id) {
    const target = String(id || "").trim().toLowerCase();
    if (!target || !db || typeof db !== "object") return "";
    const seen = new Set();
    const visit = (value, depth) => {
      if (!value || depth > 4) return "";
      if (typeof value !== "object") return "";
      if (seen.has(value)) return "";
      seen.add(value);
      if (Array.isArray(value)) {
        for (const item of value) {
          const result = visit(item, depth + 1);
          if (result) return result;
        }
        return "";
      }
      if (String(value.id || value.Id || "").trim().toLowerCase() === target) return recordDisplayName(value);
      for (const nested of Object.values(value)) {
        const result = visit(nested, depth + 1);
        if (result) return result;
      }
      return "";
    };
    return visit(db, 0);
  }
  function argumentRecordLabel(toolKey, args, db) {
    if (!args || typeof args !== "object") return "";
    const direct = argumentValue(args, [
      "title", "name", "label", "fileName", "filename", "path", "href", "url", "subject",
      "conceptTitle", "projectName", "customerName", "ticketTitle", "workPackageTitle",
      "documentName", "meetingTitle", "invoiceNumber", "expenseDescription",
    ]);
    if (direct) return direct;

    const idKeys = [
      "projectId", "customerId", "employeeId", "actionItemId", "workPackageId", "ticketId",
      "clusterId", "conceptId", "fromConceptId", "toConceptId", "sourceId", "artifactId",
      "documentId", "emailMessageId", "messageId", "meetingId", "invoiceId", "expenseId",
      "milestoneId", "deliverableId", "offerId", "sketchId", "testSuiteId", "testCaseId",
      "testStepId", "testRunId", "environmentId", "noteId",
    ];
    for (const key of idKeys) {
      const value = argumentValue(args, [key]);
      const resolved = resolveRecordName(db, value);
      if (resolved) return resolved;
      if (key === "conceptId" && value) return value;
    }

    if (toolKey.includes("search")) return argumentValue(args, ["query", "q", "search", "term"]);
    return "";
  }
  function toolActivityPath(activity, db) {
    const toolKey = activityToolKey(activity);
    const domain = toolPathDomain(toolKey);
    if (!toolKey) return null;
    if (!domain) return genericToolActivityPath(toolKey, activity);
    const mode = toolActionMode(toolKey);
    const action = toolActionLabel(toolKey, mode);
    const subject = argumentRecordLabel(toolKey, parseToolArguments(activity), db);
    const modeVerb = mode === "read" ? "Reading"
      : mode === "delete" ? "Removing"
      : mode === "link" ? "Linking"
      : mode === "move" ? "Moving"
      : mode === "run" ? "Running"
      : mode === "create" ? "Creating"
      : mode === "write" ? "Writing"
      : "Using";
    return {
      domainKey: domain.domainKey,
      mode,
      title: `${modeVerb} ${domain.root} ${domain.section.toLowerCase()}${subject ? `: ${subject}` : ""}`,
      nodes: [
        { label: domain.root, tone: "root" },
        { label: domain.section, tone: "section" },
        ...(subject ? [{ label: subject, tone: "subject" }] : []),
        { label: action, tone: mode },
      ],
    };
  }
  function genericToolActivityPath(toolKey, activity) {
    const meta = window.actionCenterActivityMeta ? window.actionCenterActivityMeta(activity) : null;
    const mode = toolKey === "exec_command" || toolKey.includes("command") || toolKey.includes("shell")
      ? "run"
      : toolActionMode(toolKey);
    const label = cleanArgumentLabel(meta && meta.label)
      || toolActionLabel(toolKey, mode);
    const command = cleanArgumentLabel(activity && activity.command);
    const message = cleanArgumentLabel(activity && activity.message);
    const subject = streamPreview(command || message, 72);
    const action = mode === "read" ? "Read"
      : mode === "delete" ? "Delete"
      : mode === "link" ? "Link"
      : mode === "move" ? "Move"
      : mode === "run" ? "Run"
      : mode === "create" ? "Create"
      : mode === "write" ? "Write"
      : "Work";
    return {
      domainKey: "codex",
      mode,
      title: `${label}${subject ? `: ${subject}` : ""}`,
      nodes: [
        { label: "Codex", tone: "root" },
        { label, tone: "section" },
        ...(subject ? [{ label: subject, tone: "subject" }] : []),
        { label: action, tone: mode },
      ],
    };
  }

  function useRunBridgeActivity(runId) {
    const [activity, setActivity] = useState(null);
    const timerRef = useRef(null);
    useEffect(() => {
      const key = String(runId || "").toLowerCase();
      if (!key) return undefined;
      const onActivity = (event) => {
        const next = event && event.detail && event.detail.activity;
        if (!next || String(next.runId || "").toLowerCase() !== key) return;
        if (timerRef.current) clearTimeout(timerRef.current);
        setActivity(next);
        timerRef.current = setTimeout(() => {
          timerRef.current = null;
          setActivity(null);
        }, BRIDGE_ACTIVITY_PULSE_MS);
      };
      window.addEventListener(ACTION_CENTER_BRIDGE_ACTIVITY_EVENT, onActivity);
      return () => {
        window.removeEventListener(ACTION_CENTER_BRIDGE_ACTIVITY_EVENT, onActivity);
        if (timerRef.current) clearTimeout(timerRef.current);
      };
    }, [runId]);
    return activity;
  }

  function toolActivityStreamKey(activity) {
    if (!activity) return "";
    return [
      activity.toolName || activity.name || "",
      activity.toolArgumentsJson || activity.toolArguments || activity.argumentsJson || activity.arguments || "",
      activity.changedAt || "",
      activity.message || "",
    ].map((part) => String(part || "")).join("|");
  }
  function isAssistantResponseActivity(activity) {
    return window.ActionCenterActivity ? window.ActionCenterActivity.isAssistantResponseActivity(activity) : false;
  }
  function useRunToolActivityStream(runId, activityOverride, db) {
    const [entries, setEntries] = useState([]);
    const timersRef = useRef({});
    const latestOverrideKeyRef = useRef("");

    const clearTimersFor = useCallback((key) => {
      ["fade", "remove"].forEach((suffix) => {
        const timerKey = `${key}:${suffix}`;
        if (timersRef.current[timerKey]) clearTimeout(timersRef.current[timerKey]);
        delete timersRef.current[timerKey];
      });
    }, []);

    const pushActivity = useCallback((activity) => {
      if (isAssistantResponseActivity(activity)) return;
      if (!activity || !toolActivityPath(activity, db)) return;
      const key = toolActivityStreamKey(activity);
      if (!key) return;
      clearTimersFor(key);
      setEntries((current) => [
        { key, activity, exiting: false },
        ...current.filter((entry) => entry.key !== key),
      ].slice(0, TOOL_ACTIVITY_STACK_MAX));
      timersRef.current[`${key}:fade`] = setTimeout(() => {
        setEntries((current) => current.map((entry) => entry.key === key ? { ...entry, exiting: true } : entry));
      }, TOOL_ACTIVITY_VISIBLE_MS);
      timersRef.current[`${key}:remove`] = setTimeout(() => {
        setEntries((current) => current.filter((entry) => entry.key !== key));
        clearTimersFor(key);
      }, TOOL_ACTIVITY_VISIBLE_MS + TOOL_ACTIVITY_FADE_MS);
    }, [clearTimersFor, db]);

    useEffect(() => {
      const key = String(runId || "").toLowerCase();
      if (!key) return undefined;
      const onActivity = (event) => {
        const next = event && event.detail && event.detail.activity;
        if (!next || String(next.runId || "").toLowerCase() !== key) return;
        pushActivity(next);
      };
      window.addEventListener(ACTION_CENTER_BRIDGE_ACTIVITY_EVENT, onActivity);
      return () => window.removeEventListener(ACTION_CENTER_BRIDGE_ACTIVITY_EVENT, onActivity);
    }, [runId, pushActivity]);

    useEffect(() => {
      const key = toolActivityStreamKey(activityOverride);
      if (!key || key === latestOverrideKeyRef.current) return;
      latestOverrideKeyRef.current = key;
      pushActivity(activityOverride);
    }, [activityOverride, pushActivity]);

    useEffect(() => () => {
      Object.values(timersRef.current).forEach((timerId) => clearTimeout(timerId));
      timersRef.current = {};
    }, []);

    return entries;
  }

  const ToolActivityBranch = React.memo(function ToolActivityBranch({ activity, position, dim, db, stackIndex = 0, connected = true, exiting = false }) {
    const path = toolActivityPath(activity, db);
    if (!path || !position) return null;
    const leftSide = position.commentSide !== "left";
    return (
      <div
        className={`av-tool-branch domain-${path.domainKey} ${path.mode} ${leftSide ? "left" : "right"} ${connected ? "connected" : "floating"} ${exiting ? "exiting" : ""} ${dim ? "dim" : ""}`}
        style={{ left: position.x + (leftSide ? -24 : 24), top: position.y - 38, "--stack-offset": `${stackIndex * 34}px` }}
        title={path.title}
        aria-hidden="true"
      >
        {path.nodes.map((node, index) => (
          <span key={`${node.label}:${index}`} className={`av-tool-node ${node.tone}`}>
            {index === 0 && <Icon name="flow" size={11} />}
            <b>{node.label}</b>
          </span>
        ))}
      </div>
    );
  });

  // Shared timer bookkeeping for the Action Center views: debounces transient run
  // refreshes (one pending load per run id) and tracks delegation edge animations,
  // clearing every pending timer on unmount.
  function useRunActivityTimers(loadTransientRun, setDelegationAnimations) {
    const delegationAnimationTimersRef = useRef({});
    const transientRunLoadTimersRef = useRef({});
    const scheduleTransientRunLoad = useCallback((runId) => {
      const key = String(runId || "").trim();
      if (!key || transientRunLoadTimersRef.current[key]) return;
      transientRunLoadTimersRef.current[key] = setTimeout(() => {
        delete transientRunLoadTimersRef.current[key];
        loadTransientRun(key);
      }, 250);
    }, [loadTransientRun]);
    const triggerDelegationAnimation = useCallback((parentRunId, childRunId, direction) => {
      const parentId = String(parentRunId || "");
      const childId = String(childRunId || "");
      if (!parentId || !childId) return;
      const edgeId = `${parentId}>${childId}`;
      if (delegationAnimationTimersRef.current[edgeId]) clearTimeout(delegationAnimationTimersRef.current[edgeId]);
      setDelegationAnimations((current) => ({ ...current, [edgeId]: direction || "handoff" }));
      delegationAnimationTimersRef.current[edgeId] = setTimeout(() => {
        delete delegationAnimationTimersRef.current[edgeId];
        setDelegationAnimations((current) => {
          if (!current[edgeId]) return current;
          const next = { ...current };
          delete next[edgeId];
          return next;
        });
      }, DELEGATION_ANIMATION_MS);
    }, [setDelegationAnimations]);
    useEffect(() => () => {
      Object.values(delegationAnimationTimersRef.current).forEach((timerId) => clearTimeout(timerId));
      Object.values(transientRunLoadTimersRef.current).forEach((timerId) => clearTimeout(timerId));
      delegationAnimationTimersRef.current = {};
      transientRunLoadTimersRef.current = {};
    }, []);
    return { scheduleTransientRunLoad, triggerDelegationAnimation };
  }

  function sideNodePosition(position) {
    const side = position && position.commentSide === "left" ? "right" : "left";
    return {
      side,
      left: (position && position.x || 0) + (side === "left" ? -34 : 34),
      top: position && position.y || 0,
    };
  }

  const RunRequestBubble = React.memo(function RunRequestBubble({ run, position, approvalRequest, selected, dim, onOpenRequest }) {
    if (!approvalRequest || !position) return null;
    const node = sideNodePosition(position);
    const title = approvalRequestPreview(approvalRequest);
    const openRequest = (event) => {
      event.stopPropagation();
      onOpenRequest(run);
    };
    return (
      <button
        type="button"
        className={`av-side-bubble av-request-bubble side-${node.side} ${selected ? "selected" : ""} ${dim ? "dim" : ""}`}
        style={{ left: node.left, top: node.top }}
        title={`Open request: ${title}`}
        aria-label={`Open request: ${title}`}
        onClick={openRequest}
      >
        <Icon name="alert" size={13} />
      </button>
    );
  }, (previous, next) =>
    previous.run === next.run
    && previous.position === next.position
    && previous.approvalRequest === next.approvalRequest
    && previous.selected === next.selected
    && previous.dim === next.dim
    && previous.onOpenRequest === next.onOpenRequest);

  const RunFinishedBubble = React.memo(function RunFinishedBubble({ run, position, selected, dim, onOpenFinished }) {
    const status = statusOf(run);
    if (!run || !["review", "done"].includes(status) || !position) return null;
    const node = sideNodePosition(position);
    const title = run.title || "Finished run";
    const review = status === "review";
    const openFinished = (event) => {
      event.stopPropagation();
      onOpenFinished(run);
    };
    return (
      <button
        type="button"
        className={`av-side-bubble ${review ? "av-review-bubble" : "av-finished-bubble"} side-${node.side} ${selected ? "selected" : ""} ${dim ? "dim" : ""}`}
        style={{ left: node.left, top: node.top }}
        title={`Open finished result: ${title}`}
        aria-label={`Open finished result: ${title}`}
        onClick={openFinished}
      >
        <Icon name={review ? "bookOpen" : "check"} size={13} />
      </button>
    );
  }, (previous, next) =>
    previous.run === next.run
    && previous.position === next.position
    && previous.selected === next.selected
    && previous.dim === next.dim
    && previous.onOpenFinished === next.onOpenFinished);

  const RunPlanPreview = React.memo(function RunPlanPreview({ steps }) {
    if (!steps || !steps.length) return null;
    const visible = steps.slice(0, 4);
    const hiddenCount = Math.max(0, steps.length - visible.length);
    return (
      <div className="av-bubble-plan" aria-label="Current plan">
        {visible.map((step, index) => (
          <div key={`${index}:${step.step}`} className={`av-bubble-plan-row ${step.status}`}>
            <span>{step.status === "done" ? <Icon name="check" size={9} /> : null}</span>
            <p>{step.step}</p>
          </div>
        ))}
        {hiddenCount > 0 && <div className="av-bubble-plan-more">+{hiddenCount} more</div>}
      </div>
    );
  }, (previous, next) => previous.steps === next.steps);

  function RunSpeechBubble({ activity, position, statusColor, statusSoft, onSelectRun }) {
    const text = speechTextFromActivity(activity);
    if (!text) return null;
    const meta = speechMetaFromActivity(activity);
    const side = position && position.commentSide === "left" ? "left" : "right";
    const style = position
      ? { left: position.x, top: position.y, "--status-color": statusColor, "--status-soft": statusSoft }
      : { "--status-color": statusColor, "--status-soft": statusSoft };
    const content = (
      <>
        <span><Icon name={meta && meta.icon ? meta.icon : "reply"} size={11} />{meta && meta.label ? meta.label : "Speaking"}</span>
        <div className="av-speech-body"><MarkdownText text={text} /><b aria-hidden="true">▍</b></div>
      </>
    );
    if (onSelectRun) {
      return (
        <button type="button" className={`av-speech-bubble side-${side}`} style={style} onClick={onSelectRun}>
          {content}
        </button>
      );
    }
    return <div className="avc-card-speech-bubble" style={style}>{content}</div>;
  }

  function RunInlineRequestCard({ run, approvalRequest, busy, onRespondApproval, className = "" }) {
    if (!approvalRequest) return null;
    const RequestCard = window.ActionCenterRequestCard;
    const respond = (requestId, decision) => {
      if (!onRespondApproval || busy) return;
      onRespondApproval(run, requestId, decision);
    };
    const stop = (event) => event.stopPropagation();
    if (RequestCard) {
      return (
        <div className={`av-inline-request-card ${className}`} onClick={stop} onMouseDown={stop}>
          <RequestCard approvalRequest={approvalRequest} busy={busy} onRespondApproval={respond} className="av-inline-request-inner" />
        </div>
      );
    }
    return (
      <div className={`av-inline-request-card ${className}`} onClick={stop} onMouseDown={stop}>
        <div className="av-inline-request-fallback">
          <span><Icon name="alert" size={12} />Needs your input</span>
          <MarkdownText text={approvalRequestPreview(approvalRequest)} />
          <div>
            <button type="button" disabled={!!busy} onClick={() => respond(approvalRequest.requestId, "approve")}>Approve</button>
            <button type="button" disabled={!!busy} onClick={() => respond(approvalRequest.requestId, "decline")}>Decline</button>
          </div>
        </div>
      </div>
    );
  }

  function RunCanvasSidePanel({ run, position, speechActivity, approvalRequest, requestBusy, statusColor, statusSoft, onSelectRun, onRespondApproval }) {
    const speech = speechTextFromActivity(speechActivity);
    if (!speech && !approvalRequest) return null;
    const side = position && position.commentSide === "left" ? "left" : "right";
    const style = {
      left: position.x,
      top: position.y,
      "--status-color": statusColor,
      "--status-soft": statusSoft,
    };
    const selectRun = (event) => {
      event.stopPropagation();
      onSelectRun(run);
    };
    return (
      <div className={`av-run-side-stack side-${side}`} style={style}>
        <RunSpeechBubble activity={speechActivity} statusColor={statusColor} statusSoft={statusSoft} onSelectRun={selectRun} />
        <RunInlineRequestCard run={run} approvalRequest={approvalRequest} busy={requestBusy} onRespondApproval={onRespondApproval} />
      </div>
    );
  }

  const RunBubble = React.memo(function RunBubble({ run, agent, position, selected, dim, activity, onSelectRun }) {
    const meta = statusMeta(statusOf(run));
    const src = sourceMeta(sourceKey(run));
    const activityMeta = window.actionCenterActivityMeta ? window.actionCenterActivityMeta(activity) : null;
    const iconName = activityMeta ? activityMeta.icon : (runStatusIcon(run) || src.icon);
    const planSteps = ["done", "review", "error"].includes(statusOf(run)) ? [] : planStepsFromActivity(activity);
    const label = bubbleTitleLabel(run);
    const title = planSteps.length ? `${label} - current plan`
        : activityMeta ? `${label} - ${activityMeta.label}` : label;
    const selectRun = (event) => {
      event.stopPropagation();
      onSelectRun(run);
    };
    const selectRunFromKeyboard = (event) => {
      if (event.key !== "Enter" && event.key !== " ") return;
      event.preventDefault();
      selectRun(event);
    };
    return (
      <div
        role="button"
        tabIndex={0}
        title={title}
        aria-label={title}
        className={`av-bubble comment-${position.commentSide || "right"} ${activityMeta ? "has-activity" : ""} ${meta.cls} ${selected ? "selected" : ""} ${dim ? "dim" : ""}`}
        style={{ left: position.x, top: position.y, "--pct": `${displayPct(run)}%`, "--status-color": meta.color, "--status-soft": meta.soft }}
        onClick={selectRun}
        onKeyDown={selectRunFromKeyboard}
      >
        <span className={activityMeta ? "av-bubble-face active-tool" : "av-bubble-face"}>
          {agent ? employeeAvatar(agent, "thread") : <Icon name={iconName} size={activityMeta ? 15 : 14} />}
          <span className="av-bubble-status" aria-hidden="true">
            <Icon name={iconName} size={activityMeta ? 12 : 11} />
          </span>
        </span>
        <span className="av-bubble-title">{label}</span>
        {planSteps.length > 0 && (
          <div className="av-bubble-side-panel">
            <RunPlanPreview steps={planSteps} />
          </div>
        )}
      </div>
    );
  }, (previous, next) =>
    previous.run === next.run
    && previous.agent === next.agent
    && previous.position === next.position
    && previous.selected === next.selected
    && previous.dim === next.dim
    && previous.activity === next.activity
    && previous.onSelectRun === next.onSelectRun);

  const RunNode = React.memo(function RunNode({ run, agent, position, selected, requestSelected, finishedSelected, dim, activityOverride, speechOverride, requestBusy, onSelectRun, onOpenRequest, onOpenFinished, onRespondApproval, db }) {
    const eventActivity = useRunBridgeActivity(run && run.id);
    const activity = activityOverride || eventActivity;
    const toolActivityEntries = useRunToolActivityStream(run && run.id, activityOverride, db);
    const approvalRequest = statusOf(run) === "done" ? null : approvalRequestFromRun(run);
    const meta = statusMeta(statusOf(run));
    return (
      <>
        {toolActivityEntries.map((entry, index) => (
          <ToolActivityBranch
            key={entry.key}
            activity={entry.activity}
            position={position}
            dim={dim}
            db={db}
            stackIndex={index}
            connected={index === 0}
            exiting={entry.exiting}
          />
        ))}
        <RunBubble
          run={run}
          agent={agent}
          position={position}
          selected={selected}
          dim={dim}
          activity={activity}
          onSelectRun={onSelectRun}
        />
        <RunCanvasSidePanel run={run} position={position} speechActivity={speechOverride} approvalRequest={approvalRequest} requestBusy={requestBusy} statusColor={meta.color} statusSoft={meta.soft} onSelectRun={onSelectRun} onRespondApproval={onRespondApproval} />
        <RunFinishedBubble
          run={run}
          position={position}
          selected={finishedSelected}
          dim={dim}
          onOpenFinished={onOpenFinished}
        />
      </>
    );
  }, (previous, next) =>
    previous.run === next.run
    && previous.agent === next.agent
    && previous.position === next.position
    && previous.selected === next.selected
    && previous.requestSelected === next.requestSelected
    && previous.finishedSelected === next.finishedSelected
    && previous.dim === next.dim
    && previous.activityOverride === next.activityOverride
    && previous.speechOverride === next.speechOverride
    && previous.requestBusy === next.requestBusy
    && previous.db === next.db
    && previous.onSelectRun === next.onSelectRun
    && previous.onOpenRequest === next.onOpenRequest
    && previous.onOpenFinished === next.onOpenFinished
    && previous.onRespondApproval === next.onRespondApproval);

  function ActivityView({ db, query, actionCenterRoute, bridgeActivityByRunId, bridgeSpeechByRunId, onToast, onError, onNav, onNeedsInputCount, onOverviewChange, onOpenActionCenterRun }) {
    const [data, setData] = useState({ agents: [], backlog: [], runs: [], taggedTickets: [], instructions: [], needsInputCount: 0 });
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState({ type: "overview", id: "" });
    const [filter, setFilter] = useState("all");
    const [itemsOpen, setItemsOpen] = useState(false);
    const [workedTicketsOpen, setWorkedTicketsOpen] = useState(false);
    const [dragItemId, setDragItemId] = useState("");
    const [dropAgentId, setDropAgentId] = useState("");
    const [busyKey, setBusyKey] = useState("");
    const [pendingAssignment, setPendingAssignment] = useState(null);
    const [selectedInstructionId, setSelectedInstructionId] = useState("");
    const [customInstructionPrompt, setCustomInstructionPrompt] = useState("");
    const [standardTaskMenu, setStandardTaskMenu] = useState(null);
    const [archivedMenu, setArchivedMenu] = useState(null);
    const [archivedQuery, setArchivedQuery] = useState("");
    const [compose, setCompose] = useState(null);
    const [composePrompt, setComposePrompt] = useState("");
    const [canvasSize, setCanvasSize] = useState({ w: 0, h: 0 });
    const [delegationAnimations, setDelegationAnimations] = useState({});
    const [requestRunId, setRequestRunId] = useState("");
    const [requestBusy, setRequestBusy] = useState(false);
    const [requestError, setRequestError] = useState("");
    const [finishedRunId, setFinishedRunId] = useState("");
    const [finishedMessages, setFinishedMessages] = useState([]);
    const [finishedLoading, setFinishedLoading] = useState(false);
    const [finishedBusyKey, setFinishedBusyKey] = useState("");
    const [finishedError, setFinishedError] = useState("");
    const canvasRef = useRef(null);
    const routeKeyRef = useRef("");

    const load = useCallback(async (silent) => {
      try {
        if (!silent) setLoading(true);
        const next = await window.API.getActionCenter();
        setData((current) => reconcileDashboard(current, next || {}));
      } catch (error) {
        if (onError) onError(error);
      } finally {
        if (!silent) setLoading(false);
      }
    }, [onError]);
    const loadWorkedTickets = useCallback(async () => {
      if (!window.API.getActionCenterTaggedTickets) return;
      try {
        const next = await window.API.getActionCenterTaggedTickets();
        setData((current) => ({
          ...current,
          taggedTickets: reconcileTaggedTickets(current.taggedTickets, Array.isArray(next) ? next : []),
        }));
      } catch (error) {
        if (onError) onError(error);
      }
    }, [onError]);
    const loadRun = useCallback(async (runId) => {
      if (!runId || !window.API.getActionCenterRun) return;
      try {
        const update = await window.API.getActionCenterRun(runId);
        setData((current) => reconcileRunUpdate(current, runId, update || {}));
        return update || null;
      } catch (error) {
        if (onError) onError(error);
      }
      return null;
    }, [onError]);
    const { scheduleTransientRunLoad, triggerDelegationAnimation } = useRunActivityTimers(loadRun, setDelegationAnimations);
    const loadFinishedRun = useCallback(async (runId) => {
      if (!runId || !window.API.getActionCenterRun) {
        setFinishedError("Action Center run endpoint is not available.");
        setFinishedMessages([]);
        return null;
      }
      try {
        setFinishedLoading(true);
        setFinishedError("");
        const update = await window.API.getActionCenterRun(runId);
        if (!update || update.exists === false) {
          setFinishedMessages([]);
          setFinishedError("This finished run no longer exists.");
          return null;
        }
        setData((current) => reconcileRunUpdate(current, runId, update || {}));
        setFinishedMessages(Array.isArray(update.messages) ? update.messages : []);
        return update;
      } catch (error) {
        setFinishedMessages([]);
        setFinishedError((error && error.message) || "Failed to load final message.");
        if (onError) onError(error);
      } finally {
        setFinishedLoading(false);
      }
      return null;
    }, [onError]);

    useEffect(() => { load(false); }, [load]);
    useEffect(() => {
      if (onNeedsInputCount) onNeedsInputCount(Number(data.needsInputCount) || 0);
    }, [data.needsInputCount, onNeedsInputCount]);
    useEffect(() => {
      if (onOverviewChange) onOverviewChange({ agents: data.agents || [], runs: data.runs || [], taggedTickets: data.taggedTickets || [], needsInputCount: Number(data.needsInputCount) || 0 });
    }, [data, onOverviewChange]);
    useEffect(() => {
      const node = canvasRef.current;
      if (!node) return undefined;
      const measure = () => setCanvasSize({ w: Math.round(node.clientWidth), h: Math.round(node.clientHeight) });
      measure();
      const observer = new ResizeObserver(measure);
      observer.observe(node);
      return () => observer.disconnect();
    }, [loading, (data.agents || []).length]);
    useEffect(() => {
      if (!window.API.subscribeToChanges) {
        return undefined;
      }
      const unsubscribe = window.API.subscribeToChanges((change) => {
        if (isWorkedTicketRefreshEvent(change)) {
          loadWorkedTickets();
          return;
        }
        if (change && change.kind === "employee-activity" && change.jobId) {
          const activityKind = norm(change.toolName);
          if (activityKind === "transient-activity" || activityKind === "codex-progress") {
            scheduleTransientRunLoad(change.jobId);
            return;
          }
          if (isDelegationEvent(change)) {
            const direction = delegationDirection(change);
            loadRun(change.jobId).then((update) => {
              const run = update && update.run;
              const parentId = runParentId(run);
              if (!run || !parentId) return;
              loadRun(parentId);
              triggerDelegationAnimation(parentId, run.id, direction);
            });
            return;
          }
          if (isActionCenterEvent(change) && isDurableActionCenterEvent(change)) {
            loadRun(change.jobId);
          }
          return;
        }
      });
      return () => unsubscribe();
    }, [loadRun, loadWorkedTickets, scheduleTransientRunLoad, triggerDelegationAnimation]);
    useEffect(() => {
      if (!standardTaskMenu && !archivedMenu) return undefined;
      const isFloatingMenuTarget = (target) => !!(target && target.closest && target.closest(".ac-standard-task-menu, .ac-archived-thread-menu"));
      const close = () => {
        setStandardTaskMenu(null);
        setArchivedMenu(null);
      };
      const onKey = (event) => { if (event.key === "Escape") close(); };
      const onScroll = (event) => {
        if (isFloatingMenuTarget(event.target)) return;
        close();
      };
      window.addEventListener("mousedown", close);
      window.addEventListener("scroll", onScroll, true);
      window.addEventListener("resize", close);
      window.addEventListener("keydown", onKey);
      return () => {
        window.removeEventListener("mousedown", close);
        window.removeEventListener("scroll", onScroll, true);
        window.removeEventListener("resize", close);
        window.removeEventListener("keydown", onKey);
      };
    }, [standardTaskMenu, archivedMenu]);

    const agents = data.agents || [];
    const mentionOptions = useMemo(() => window.buildMentionOptions ? window.buildMentionOptions(db) : [], [db]);
    const runs = useMemo(() => (data.runs || []).filter((run) => !run.archived).slice().sort(runSort), [data.runs]);
    const runsById = useMemo(() => {
      const map = {};
      runs.forEach((run) => { map[run.id] = run; });
      return map;
    }, [runs]);
    const agentsById = useMemo(() => {
      const map = {};
      agents.forEach((agent) => { map[agent.id] = agent; });
      return map;
    }, [agents]);
    const requestRun = useMemo(() => (requestRunId ? runs.find((run) => sameId(run.id, requestRunId)) || null : null), [runs, requestRunId]);
    const requestApproval = useMemo(() => approvalRequestFromRun(requestRun), [requestRun]);
    const finishedRun = useMemo(() => (finishedRunId ? runs.find((run) => sameId(run.id, finishedRunId)) || null : null), [runs, finishedRunId]);
    const finishedMessage = useMemo(() => finalAgentMessage(finishedMessages, finishedRun), [finishedMessages, finishedRun]);
    const runsByAgent = useMemo(() => {
      const grouped = {};
      runs.forEach((run) => {
        const key = run.employeeId || run.agentId;
        if (!grouped[key]) grouped[key] = [];
        grouped[key].push(run);
      });
      Object.keys(grouped).forEach((key) => grouped[key].sort(runSort));
      return grouped;
    }, [runs]);
    const backlogById = useMemo(() => {
      const map = {};
      (data.backlog || []).forEach((item) => { map[item.id] = item; });
      return map;
    }, [data.backlog]);
    const searchedBacklog = useMemo(() => {
      const q = norm(query);
      return (data.backlog || []).filter((item) => !q || [item.title, item.detail, item.projectName, item.customerName, item.type, item.priority].join(" ").toLowerCase().includes(q));
    }, [data.backlog, query]);
    const searchedWorkedTickets = useMemo(() => {
      const q = norm(query);
      return (data.taggedTickets || []).filter((ticket) => !q || [
        ticket.ticketTitle,
        ticket.ticketStatus,
        ticket.projectName,
        ticket.employeeName,
        ticket.runTitle,
        ticket.threadId,
        ticket.ticketNumber ? `#${ticket.ticketNumber}` : "",
      ].join(" ").toLowerCase().includes(q));
    }, [data.taggedTickets, query]);
    const workedTicketGroups = useMemo(() => {
      const order = ["In progress", "Ready for review", "Open", "Done"];
      const groups = new Map();
      searchedWorkedTickets.forEach((ticket) => {
        const status = String(ticket.ticketStatus || "Open").trim() || "Open";
        if (!groups.has(status)) groups.set(status, []);
        groups.get(status).push(ticket);
      });
      groups.forEach((tickets) => tickets.sort((a, b) =>
        String(a.projectName || "").localeCompare(String(b.projectName || ""))
        || (Number(a.ticketNumber) || 0) - (Number(b.ticketNumber) || 0)
        || String(a.ticketTitle || "").localeCompare(String(b.ticketTitle || ""))));
      return Array.from(groups.entries())
        .sort(([a], [b]) => {
          const ai = order.findIndex((item) => item.toLowerCase() === a.toLowerCase());
          const bi = order.findIndex((item) => item.toLowerCase() === b.toLowerCase());
          return (ai < 0 ? 99 : ai) - (bi < 0 ? 99 : bi) || a.localeCompare(b);
        })
        .map(([status, tickets]) => ({ status, tickets }));
    }, [searchedWorkedTickets]);

    useEffect(() => {
      const routeEmployeeId = actionCenterRoute && actionCenterRoute.employeeId;
      const routeRunId = actionCenterRoute && actionCenterRoute.runId;
      const key = `${routeEmployeeId || ""}:${routeRunId || ""}`;
      if (!key.trim() || routeKeyRef.current === key) return;
      const routeRun = routeRunId ? runs.find((run) => sameId(run.id, routeRunId)) : null;
      if (routeRun) setSelected({ type: "run", id: routeRun.id });
      else if (routeEmployeeId && agents.some((agent) => sameId(agent.id, routeEmployeeId))) setSelected({ type: "employee", id: routeEmployeeId });
      routeKeyRef.current = key;
    }, [actionCenterRoute && actionCenterRoute.employeeId, actionCenterRoute && actionCenterRoute.runId, agents, runs]);

    const filteredRuns = useMemo(() => runs.filter((run) => filter === "all" || (filter === "error" ? statusOf(run) === "error" : statusOf(run) === filter)), [runs, filter]);
    const selectedEdge = selected.type === "edge" ? selected.id : "";
    const edgeRuns = runs.filter((run) => runParentId(run) && runsById[runParentId(run)]);
    const emphasis = useMemo(() => {
      if (selected.type === "run" && selected.id) {
        const set = new Set([selected.id]);
        let current = runsById[selected.id];
        while (current && runParentId(current) && runsById[runParentId(current)]) {
          const parentId = runParentId(current);
          set.add(parentId);
          current = runsById[parentId];
        }
        const addChildren = (id) => edgeRuns.filter((run) => runParentId(run) === id).forEach((child) => {
          set.add(child.id);
          addChildren(child.id);
        });
        addChildren(selected.id);
        return set;
      }
      if (selected.type === "employee" && selected.id) return new Set((runsByAgent[selected.id] || []).map((run) => run.id));
      if (selected.type === "edge" && selected.id) return new Set(selected.id.split(">"));
      return null;
    }, [selected, runsById, runsByAgent, edgeRuns]);

    const layout = useMemo(() => {
      const cw = canvasSize.w || 900;
      const ch = canvasSize.h || 620;
      const leftInset = itemsOpen ? 326 : 86;
      const topPad = 86;
      const botPad = 128;
      const rightPad = 160;
      const maxRunsPerAgent = Math.max(0, ...Object.keys(runsByAgent).map((agentId) => (runsByAgent[agentId] || []).length));
      const denseThreadLayout = maxRunsPerAgent > 1 || edgeRuns.length > 0;
      const attachedBubbleRadius = denseThreadLayout ? 138 : 96;
      const bubbleRingGap = denseThreadLayout ? 104 : 62;
      const bubbleOrbit = attachedBubbleRadius + Math.max(0, Math.ceil((maxRunsPerAgent - 3) / 3)) * bubbleRingGap;
      const cellW = denseThreadLayout
        ? Math.max(680, Math.min(1180, bubbleOrbit * 2 + 430))
        : Math.max(360, Math.min(760, bubbleOrbit * 2 + 160));
      const delegatedAgentCellW = cellW + (denseThreadLayout ? 260 : 160);
      const cellH = denseThreadLayout
        ? Math.max(460, Math.min(840, bubbleOrbit * 2 + 260))
        : Math.max(300, Math.min(560, bubbleOrbit * 2 + 170));
      const idleCellW = Math.max(170, Math.min(220, cellW - 190));
      const idleCellH = 148;
      const agentBoxMetrics = { left: 92, right: 92, top: 76, bottom: 126 };
      const bubblePanelMetrics = { left: 52, right: 356, top: 102, bottom: 102 };
      const componentGap = denseThreadLayout ? 160 : 96;
      const componentRowGap = denseThreadLayout ? 150 : 104;
      const idleAgents = agents.filter((agent) => !(runsByAgent[agent.id] || []).length);
      const workAgents = agents.filter((agent) => (runsByAgent[agent.id] || []).length);
      const minWorldW = leftInset + rightPad + cellW;
      const workAgentById = {};
      const workAgentIds = new Set(workAgents.map((agent) => agent.id));
      workAgents.forEach((agent) => { workAgentById[agent.id] = agent; });
      const predByAgent = {};
      edgeRuns.forEach((run) => {
        const parent = runsById[runParentId(run)];
        const fromId = parent && (parent.employeeId || parent.agentId);
        const toId = run.employeeId || run.agentId;
        if (!parent || !fromId || !toId || fromId === toId) return;
        if (!predByAgent[toId]) predByAgent[toId] = new Set();
        predByAgent[toId].add(fromId);
      });
      const tierMemo = {};
      const tierFor = (agentId, stack = new Set()) => {
        if (tierMemo[agentId] != null) return tierMemo[agentId];
        if (stack.has(agentId)) return 0;
        stack.add(agentId);
        const preds = Array.from(predByAgent[agentId] || []);
        const tier = preds.length ? 1 + Math.max(...preds.map((id) => tierFor(id, stack))) : 0;
        stack.delete(agentId);
        tierMemo[agentId] = tier;
        return tier;
      };
      workAgents.forEach((agent) => tierFor(agent.id));
      const availableW = Math.max(cellW, cw - leftInset - rightPad);
      const colsFor = (count, gridCellW = cellW) => Math.max(1, Math.min(count || 1, Math.floor(availableW / gridCellW) || 1));
      const idleCols = colsFor(idleAgents.length, idleCellW);
      const idleRows = idleAgents.length ? Math.ceil(idleAgents.length / idleCols) : 0;
      const idleBlockH = idleRows ? idleRows * idleCellH : 0;
      const workTopPad = topPad + idleBlockH;

      const adjacentByAgent = {};
      workAgents.forEach((agent) => { adjacentByAgent[agent.id] = new Set(); });
      Object.keys(predByAgent).forEach((toId) => {
        if (!workAgentIds.has(toId)) return;
        Array.from(predByAgent[toId] || []).forEach((fromId) => {
          if (!workAgentIds.has(fromId)) return;
          adjacentByAgent[toId].add(fromId);
          adjacentByAgent[fromId].add(toId);
        });
      });
      const visitedAgents = new Set();
      const workComponents = [];
      workAgents.forEach((agent) => {
        if (visitedAgents.has(agent.id)) return;
        const ids = [];
        const queue = [agent.id];
        visitedAgents.add(agent.id);
        while (queue.length) {
          const id = queue.shift();
          ids.push(id);
          Array.from(adjacentByAgent[id] || []).forEach((nextId) => {
            if (visitedAgents.has(nextId)) return;
            visitedAgents.add(nextId);
            queue.push(nextId);
          });
        }
        const minTier = Math.min(...ids.map((id) => tierMemo[id] || 0));
        const tiers = {};
        ids.forEach((id) => {
          const tier = (tierMemo[id] || 0) - minTier;
          if (!tiers[tier]) tiers[tier] = [];
          tiers[tier].push(id);
        });
        const maxLocalTier = Math.max(0, ...Object.keys(tiers).map(Number));
        const tierCellW = maxLocalTier > 0 ? delegatedAgentCellW : cellW;
        const rowCount = Math.max(1, ...Object.values(tiers).map((items) => items.length));
        const names = ids.map((id) => (workAgentById[id] && workAgentById[id].name) || "").sort();
        workComponents.push({
          tiers,
          colCount: maxLocalTier + 1,
          rowCount,
          tierCellW,
          width: (maxLocalTier + 1) * tierCellW,
          height: rowCount * cellH,
          maxLocalTier,
          name: names[0] || "",
        });
      });
      workComponents.sort((a, b) => b.maxLocalTier - a.maxLocalTier || a.name.localeCompare(b.name));

      const workComponentRows = [];
      workComponents.forEach((component) => {
        let row = workComponentRows[workComponentRows.length - 1];
        const nextWidth = row ? row.width + componentGap + component.width : component.width;
        if (row && nextWidth > availableW) row = null;
        if (!row) {
          row = { components: [], width: 0, height: 0 };
          workComponentRows.push(row);
        }
        row.width = row.components.length ? row.width + componentGap + component.width : component.width;
        row.height = Math.max(row.height, component.height);
        row.components.push(component);
      });
      const maxWorkRowW = Math.max(0, ...workComponentRows.map((row) => row.width));
      const workNeededH = workComponentRows.reduce((sum, row, index) => sum + row.height + (index ? componentRowGap : 0), 0);
      let worldW = Math.max(cw, minWorldW, leftInset + rightPad + maxWorkRowW);
      const usableW = Math.max(cellW, worldW - leftInset - rightPad);
      const worldH = Math.max(ch, workTopPad + botPad + workNeededH);
      const positions = {};
      const usableH = Math.max(1, worldH - workTopPad - botPad);

      function clamp(value, min, max) {
        if (max < min) return min;
        return Math.max(min, Math.min(max, value));
      }
      function boxAt(point, metrics) {
        return {
          left: point.x - metrics.left,
          right: point.x + metrics.right,
          top: point.y - metrics.top,
          bottom: point.y + metrics.bottom,
        };
      }
      function commentSideFor(point) {
        return point.x > worldW - rightPad - 420 ? "left" : "right";
      }
      function runBoxAt(point, commentSide) {
        const metrics = commentSide === "left"
          ? { left: bubblePanelMetrics.right, right: bubblePanelMetrics.left, top: bubblePanelMetrics.top, bottom: bubblePanelMetrics.bottom }
          : bubblePanelMetrics;
        return boxAt(point, metrics);
      }
      function intersects(a, b, gap = 0) {
        return a.left - gap < b.right
          && a.right + gap > b.left
          && a.top - gap < b.bottom
          && a.bottom + gap > b.top;
      }
      function distanceToBox(box, other) {
        const dx = box.right < other.left ? other.left - box.right : other.right < box.left ? box.left - other.right : 0;
        const dy = box.bottom < other.top ? other.top - box.bottom : other.bottom < box.top ? box.top - other.bottom : 0;
        return Math.sqrt(dx * dx + dy * dy);
      }
      function angleDistance(a, b) {
        let diff = Math.abs(a - b) % (Math.PI * 2);
        if (diff > Math.PI) diff = Math.PI * 2 - diff;
        return diff;
      }
      function averageAngle(angles) {
        if (!angles || !angles.length) return null;
        const vector = angles.reduce((sum, angle) => ({
          x: sum.x + Math.cos(angle),
          y: sum.y + Math.sin(angle),
        }), { x: 0, y: 0 });
        if (Math.abs(vector.x) < 0.001 && Math.abs(vector.y) < 0.001) return angles[0];
        return Math.atan2(vector.y, vector.x);
      }
      function addAngleHint(map, runId, angle) {
        if (!runId || !Number.isFinite(angle)) return;
        if (!map[runId]) map[runId] = [];
        map[runId].push(angle);
      }

      function placeGrid(list, startY, areaH, cols, gridCellW = cellW, gridCellH = cellH) {
        if (!list.length) return;
        const rowCount = Math.ceil(list.length / cols);
        const gridW = (cols - 1) * gridCellW;
        const gridH = (rowCount - 1) * gridCellH;
        const startX = leftInset + usableW / 2 - gridW / 2;
        const y0 = startY + Math.max(gridCellH / 2, (areaH - gridH) / 2);
        list.forEach((agent, index) => {
          const col = index % cols;
          const row = Math.floor(index / cols);
          positions[agent.id] = { x: startX + col * gridCellW, y: y0 + row * gridCellH };
        });
      }

      placeGrid(idleAgents.slice().sort((a, b) => String(a.name).localeCompare(String(b.name))), topPad, idleBlockH || idleCellH, idleCols, idleCellW, idleCellH);

      function placeComponent(component, startX, startY, rowH) {
        Object.keys(component.tiers).map(Number).sort((a, b) => a - b).forEach((tier) => {
          const list = component.tiers[tier].slice().sort((aId, bId) => {
            const avg = (id) => {
              const preds = Array.from(predByAgent[id] || []);
              const ys = preds.map((id) => positions[id] && positions[id].y).filter(Boolean);
              return ys.length ? ys.reduce((sum, y) => sum + y, 0) / ys.length : 0;
            };
            const a = workAgentById[aId] || {};
            const b = workAgentById[bId] || {};
            return avg(aId) - avg(bId) || String(a.name).localeCompare(String(b.name));
          });
          const tierCellW = component.tierCellW || cellW;
          const x = startX + tier * tierCellW + tierCellW / 2;
          const gridH = (list.length - 1) * cellH;
          const y0 = startY + Math.max(cellH / 2, (rowH - gridH) / 2);
          list.forEach((agentId, index) => {
            const jitter = component.colCount > 1 ? ((index % 2 === 0) ? 18 : -18) * (tier % 2 === 0 ? 1 : -1) : 0;
            positions[agentId] = { x: Math.max(leftInset + tierCellW / 2, x + jitter), y: y0 + cellH * index };
          });
        });
      }

      if (workComponentRows.length) {
        const totalWorkH = workComponentRows.reduce((sum, row, index) => sum + row.height + (index ? componentRowGap : 0), 0);
        let rowY = workTopPad + Math.max(0, (usableH - totalWorkH) / 2);
        workComponentRows.forEach((row) => {
          let componentX = leftInset + usableW / 2 - row.width / 2;
          row.components.forEach((component) => {
            placeComponent(component, componentX, rowY, row.height);
            componentX += component.width + componentGap;
          });
          rowY += row.height + componentRowGap;
        });
      }

      const agentBoxes = {};
      Object.keys(positions).forEach((agentId) => {
        const pos = positions[agentId];
        const clamped = {
          x: clamp(pos.x, leftInset + agentBoxMetrics.left, worldW - rightPad - agentBoxMetrics.right),
          y: clamp(pos.y, topPad + agentBoxMetrics.top, worldH - botPad - agentBoxMetrics.bottom),
        };
        positions[agentId] = clamped;
        agentBoxes[agentId] = boxAt(clamped, agentBoxMetrics);
      });

      const angleHintsByRun = {};
      edgeRuns.forEach((run, index) => {
        const parent = runsById[runParentId(run)];
        if (!parent) return;
        const fromAgentId = parent.employeeId || parent.agentId;
        const toAgentId = run.employeeId || run.agentId;
        const from = positions[fromAgentId];
        const to = positions[toAgentId];
        if (!from || !to) return;
        const sameAgent = fromAgentId === toAgentId;
        const baseAngle = sameAgent
          ? (index % 2 === 0 ? 0 : Math.PI)
          : Math.atan2(to.y - from.y, to.x - from.x);
        addAngleHint(angleHintsByRun, parent.id, baseAngle);
        addAngleHint(angleHintsByRun, run.id, baseAngle + Math.PI);
      });

      const bubbles = {};
      const occupiedBoxes = Object.keys(agentBoxes).map((agentId) => ({ kind: "agent", agentId, box: agentBoxes[agentId] }));
      const preferredAngles = [-Math.PI / 2, -Math.PI / 4, -Math.PI * 3 / 4, 0, Math.PI, -Math.PI * 5 / 6, Math.PI * 3 / 4, Math.PI / 4];
      Object.keys(runsByAgent).forEach((agentId) => {
        const pos = positions[agentId];
        if (!pos) return;
        const agentRuns = runsByAgent[agentId] || [];
        agentRuns.forEach((run, index) => {
          const hintedAngle = averageAngle(angleHintsByRun[run.id]);
          const baseAngle = hintedAngle == null ? preferredAngles[index % preferredAngles.length] : hintedAngle;
          const candidateAngles = [0, -0.34, 0.34, -0.68, 0.68, -1.05, 1.05, -1.45, 1.45, Math.PI]
            .map((offset) => baseAngle + offset);
          let best = null;
          [0, 1, 2, 3, 4, 5].forEach((ring) => {
            const radius = attachedBubbleRadius + ring * bubbleRingGap + Math.floor(index / 8) * 10;
            candidateAngles.forEach((angle) => {
              const raw = {
                x: pos.x + Math.cos(angle) * radius,
                y: pos.y + Math.sin(angle) * radius,
              };
              const side = commentSideFor(raw);
              const metrics = side === "left"
                ? { left: bubblePanelMetrics.right, right: bubblePanelMetrics.left, top: bubblePanelMetrics.top, bottom: bubblePanelMetrics.bottom }
                : bubblePanelMetrics;
              const point = {
                x: clamp(raw.x, leftInset + metrics.left, worldW - rightPad - metrics.right),
                y: clamp(raw.y, topPad + metrics.top, worldH - botPad - metrics.bottom),
              };
              const finalSide = commentSideFor(point);
              const box = runBoxAt(point, finalSide);
              const blockers = occupiedBoxes.filter((other) => other.kind !== "agent" || other.agentId !== agentId);
              const collisions = blockers.reduce((sum, other) => sum + (intersects(box, other.box, 18) ? 1 : 0), 0);
              const distancePenalty = blockers.reduce((sum, other) => sum + Math.max(0, 48 - distanceToBox(box, other.box)), 0);
              const score = collisions * 10000 + distancePenalty * 12 + ring * 150 + angleDistance(angle, baseAngle) * (hintedAngle == null ? 26 : 120);
              if (!best || score < best.score) best = { point, box, commentSide: finalSide, score };
            });
          });
          const point = best ? best.point : { x: pos.x + attachedBubbleRadius, y: pos.y };
          const commentSide = best && best.commentSide ? best.commentSide : commentSideFor(point);
          bubbles[run.id] = { x: point.x, y: point.y, commentSide };
          occupiedBoxes.push({ kind: "bubble", runId: run.id, box: best ? best.box : runBoxAt(point, commentSide) });
        });
      });

      const edgeLabels = {};
      edgeRuns.forEach((run) => {
        const parent = runsById[runParentId(run)];
        const from = parent && bubbles[parent.id];
        const to = bubbles[run.id];
        if (!parent || !from || !to) return;
        const dx = to.x - from.x;
        const dy = to.y - from.y;
        const len = Math.max(1, Math.sqrt(dx * dx + dy * dy));
        edgeLabels[`${parent.id}>${run.id}`] = {
          x: clamp((from.x + to.x) / 2 - (dy / len) * 30, leftInset + 88, worldW - rightPad - 88),
          y: clamp((from.y + to.y) / 2 + (dx / len) * 30, topPad + 30, worldH - botPad - 30),
        };
      });

      return { worldW, worldH, positions, bubbles, edgeLabels };
    }, [agents, runsByAgent, runsById, edgeRuns, canvasSize, itemsOpen]);

    const counts = useMemo(() => ({
      all: runs.length,
      working: runs.filter((run) => ["working", "waiting-delegates"].includes(statusOf(run))).length,
      "needs-input": runs.filter((run) => statusOf(run) === "needs-input").length,
      review: runs.filter((run) => statusOf(run) === "review").length,
      done: runs.filter((run) => statusOf(run) === "done").length,
      error: runs.filter((run) => statusOf(run) === "error").length,
    }), [runs]);

    const instructionsFor = useCallback((item) => {
      const type = norm(item && item.type);
      const all = data.instructions || [];
      const exact = all.filter((instruction) => norm(instruction.actionItemType) === type);
      const fallback = all.filter((instruction) => norm(instruction.actionItemType) === "default");
      return (exact.length ? exact : fallback).slice().sort((a, b) => (a.position || 0) - (b.position || 0));
    }, [data.instructions]);
    const standardTasksFor = useCallback((employee) => ((employee && employee.standardTasks) || [])
      .filter((task) => task && String(task.title || "").trim() && String(task.prompt || "").trim())
      .slice()
      .sort((a, b) => (Number(a.position) || 0) - (Number(b.position) || 0)), []);
    const scopedEntities = useCallback((scopeType) => {
      const clean = norm(scopeType);
      if (clean === "project") return ((db && db.projects) || []).slice().sort((a, b) => String(a.name || "").localeCompare(String(b.name || "")));
      if (clean === "customer") return ((db && db.customers) || []).slice().sort((a, b) => String(a.name || "").localeCompare(String(b.name || "")));
      return [];
    }, [db]);
    const workPackagesForProject = useCallback((project) => ((project && project.workPackages) || [])
      .slice()
      .sort((a, b) => (Number(a.position) || 0) - (Number(b.position) || 0) || String(a.name || "").localeCompare(String(b.name || ""))), []);
    const standardTaskAllowsInput = useCallback((task) => !!(task && task.additionalInput), []);
    const standardTaskRequiresWorkPackage = useCallback((task) => norm(task && task.scopeType) === "project" && !!(task && task.workPackageScope), []);
    const standardTaskActionLabel = useCallback((task) => {
      if (task && task.scopeType) return `Select ${String(task.scopeType).toLowerCase()}`;
      return standardTaskAllowsInput(task) ? "Add custom prompt" : "Start now";
    }, [standardTaskAllowsInput]);
    const standardTaskPrompt = useCallback((task, additionalInput) => {
      const prompt = String(task && task.prompt || "").trim();
      const input = String(additionalInput || "").trim();
      return input ? `${prompt}\n\nCustom prompt:\n${input}` : prompt;
    }, []);
    const contextReferenceForStandardTask = useCallback((employee, task, entity, workPackage) => {
      const scopeType = String(task && task.scopeType || "").trim();
      const title = String(task && task.title || "Standard task").trim();
      const baseFields = {
        Employee: (employee && employee.name) || "",
        EmployeeId: (employee && employee.id) || "",
        Role: (employee && employee.role) || "AI employee",
        StandardTask: title,
      };
      if (norm(scopeType) === "project" && entity) {
        const customerId = (entity.customerIds || [])[0] || "";
        const customer = ((db && db.customers) || []).find((item) => item.id === customerId);
        if (workPackage) {
          return {
            type: "Project work package",
            id: workPackage.id || "",
            label: `${title}: ${entity.name || "Project"} / ${workPackage.name || "Work package"}`,
            url: `${currentUrlWithoutHash()}#projects/${encodeURIComponent(entity.id || "")}`,
            fields: { ...baseFields, Project: entity.name || "", ProjectId: entity.id || "", Customer: (customer && customer.name) || "", CustomerId: customerId, WorkPackage: workPackage.name || "", WorkPackageTitle: workPackage.name || "", WorkPackageId: workPackage.id || "" },
          };
        }
        return {
          type: "Project",
          id: entity.id || "",
          label: `${title}: ${entity.name || "Project"}`,
          url: `${currentUrlWithoutHash()}#projects/${encodeURIComponent(entity.id || "")}`,
          fields: { ...baseFields, Project: entity.name || "", ProjectId: entity.id || "", Customer: (customer && customer.name) || "", CustomerId: customerId },
        };
      }
      if (norm(scopeType) === "customer" && entity) {
        return {
          type: "Customer",
          id: entity.id || "",
          label: `${title}: ${entity.name || "Customer"}`,
          url: currentUrlWithoutHash(),
          fields: { ...baseFields, Customer: entity.name || "", CustomerId: entity.id || "" },
        };
      }
      return {
        ...activityReferenceFor(employee),
        label: title,
        fields: baseFields,
      };
    }, [db]);
    async function withBusy(key, work, options = {}) {
      const reloadAfter = options.reloadAfter !== false;
      try {
        setBusyKey(key);
        await work();
        if (reloadAfter) await load(true);
      } catch (error) {
        if (onError) onError(error);
      } finally {
        setBusyKey("");
      }
    }
    const assign = useCallback((itemId, employeeId) => {
      if (!itemId || !employeeId) return;
      const item = backlogById[itemId];
      const options = instructionsFor(item);
      setPendingAssignment({ itemId, employeeId });
      setSelectedInstructionId(options[0] ? options[0].id : "");
      setCustomInstructionPrompt("");
    }, [backlogById, instructionsFor]);
    const confirmAssignment = useCallback(() => {
      const useCustom = selectedInstructionId === CUSTOM_INSTRUCTION_ID;
      const customPrompt = customInstructionPrompt.trim();
      if (!pendingAssignment || (useCustom ? !customPrompt : !selectedInstructionId)) return;
      const { itemId, employeeId } = pendingAssignment;
      withBusy(`assign:${itemId}:${employeeId}`, async () => {
        const run = await window.API.assignActionCenterRun(itemId, employeeId, useCustom ? "" : selectedInstructionId, useCustom ? customPrompt : "");
        setData((current) => ({
          ...current,
          backlog: (current.backlog || []).filter((item) => item.id !== itemId),
          runs: reconcileList(current.runs, [...(current.runs || []).filter((item) => item.id !== run.id), run].sort(runSort), (item) => item.id),
        }));
        setPendingAssignment(null);
        setSelectedInstructionId("");
        setCustomInstructionPrompt("");
        setSelected({ type: "run", id: run.id || "" });
        if (onOpenActionCenterRun && run.id) onOpenActionCenterRun(run.employeeId || employeeId, run.id);
        if (run.id) await loadRun(run.id);
        if (onToast) onToast("Action item assigned");
      }, { reloadAfter: false });
    }, [pendingAssignment, selectedInstructionId, customInstructionPrompt, loadRun, onOpenActionCenterRun, onToast]);
    const openStartComposer = useCallback((employee) => {
      if (!employee) return;
      setCompose({ employee, reference: activityReferenceFor(employee) });
      setComposePrompt("");
    }, []);
    const openStandardTaskMenu = useCallback((employee, event) => {
      const tasks = standardTasksFor(employee);
      if (!employee || tasks.length === 0) {
        openStartComposer(employee);
        return;
      }
      const rect = event && event.currentTarget && event.currentTarget.getBoundingClientRect ? event.currentTarget.getBoundingClientRect() : null;
      const width = 420;
      const margin = 10;
      const maxLeft = Math.max(margin, window.innerWidth - width - margin);
      const maxTop = Math.max(margin, window.innerHeight - margin - 560);
      setStandardTaskMenu({
        employee,
        mode: "tasks",
        task: null,
        x: rect ? Math.min(Math.max(margin, rect.left - width + rect.width), maxLeft) : margin,
        y: rect ? Math.min(Math.max(margin, rect.bottom + 8), maxTop) : margin,
      });
    }, [openStartComposer, standardTasksFor]);
    const openArchivedThreadsMenu = useCallback((employee, event) => {
      if (!employee) return;
      const rect = event && event.currentTarget && event.currentTarget.getBoundingClientRect ? event.currentTarget.getBoundingClientRect() : null;
      const width = 340;
      const margin = 10;
      const nextMenu = {
        employee,
        x: rect ? Math.min(Math.max(margin, rect.left), window.innerWidth - width - margin) : margin,
        y: rect ? Math.min(Math.max(margin, rect.bottom + 8), window.innerHeight - margin - 430) : margin,
        loading: true,
        error: "",
        threads: [],
      };
      setStandardTaskMenu(null);
      setArchivedQuery("");
      setArchivedMenu(nextMenu);
      if (!window.API.listArchivedActionCenterThreads) {
        setArchivedMenu({ ...nextMenu, loading: false, error: "Archived thread endpoint is not available." });
        return;
      }
      window.API.listArchivedActionCenterThreads(employee.id)
        .then((threads) => {
          setArchivedMenu((current) => current && current.employee && sameId(current.employee.id, employee.id)
            ? { ...current, loading: false, error: "", threads: Array.isArray(threads) ? threads : [] }
            : current);
        })
        .catch((error) => {
          setArchivedMenu((current) => current && current.employee && sameId(current.employee.id, employee.id)
            ? { ...current, loading: false, error: (error && error.message) || "Failed to load archived threads.", threads: [] }
            : current);
          if (onError) onError(error);
        });
    }, [onError]);
    const archivedThreads = useMemo(() => {
      const q = norm(archivedQuery);
      const threads = archivedMenu && Array.isArray(archivedMenu.threads) ? archivedMenu.threads : [];
      if (!q) return threads;
      return threads.filter((thread) => [thread.title, thread.preview, thread.detail, thread.projectName, thread.customerName, thread.contextScopeLabel, thread.threadId].join(" ").toLowerCase().includes(q));
    }, [archivedMenu, archivedQuery]);
    const submitStandardTask = useCallback((employee, task, entity, options = {}) => {
      const prompt = standardTaskPrompt(task, options.additionalInput);
      if (!employee || !prompt || busyKey) return;
      const reference = contextReferenceForStandardTask(employee, task, entity, options.workPackage);
      withBusy(`standard-task:${employee.id}:${task.id || task.title}`, async () => {
        const run = await window.API.startActionCenterContextTask(employee.id, prompt, reference);
        setStandardTaskMenu(null);
        await load(true);
        if (run && run.id) {
          setSelected({ type: "run", id: run.id });
          await loadRun(run.id);
        }
        if (onToast) onToast("Action Center task queued");
      }, { reloadAfter: false });
    }, [busyKey, contextReferenceForStandardTask, load, loadRun, onToast, standardTaskPrompt]);
    const submitStartComposer = useCallback(() => {
      const clean = composePrompt.trim();
      if (!compose || !clean || busyKey) return;
      withBusy(`context:${compose.employee.id}`, async () => {
        const run = await window.API.startActionCenterContextTask(compose.employee.id, clean, compose.reference);
        setCompose(null);
        setComposePrompt("");
        await load(true);
        if (run && run.id) {
          setSelected({ type: "run", id: run.id });
          await loadRun(run.id);
        }
        if (onToast) onToast("Action Center task queued");
      }, { reloadAfter: false });
    }, [compose, composePrompt, busyKey, load, loadRun, onToast]);
    const selectRun = useCallback((run) => {
      if (!run) return;
      setSelected({ type: "run", id: run.id });
      loadRun(run.id);
      if (onOpenActionCenterRun) onOpenActionCenterRun(run.employeeId || run.agentId, run.id);
    }, [loadRun, onOpenActionCenterRun]);
    const openRequest = useCallback((run) => {
      if (!run) return;
      setSelected({ type: "run", id: run.id });
      setRequestRunId(run.id || "");
      setRequestError("");
      setFinishedRunId("");
      loadRun(run.id);
    }, [loadRun]);
    const openFinished = useCallback((run) => {
      if (!run) return;
      setSelected({ type: "run", id: run.id });
      setFinishedRunId(run.id || "");
      setFinishedMessages([]);
      setFinishedError("");
      setRequestRunId("");
      loadFinishedRun(run.id);
    }, [loadFinishedRun]);
    const closeFinished = useCallback(() => {
      if (finishedBusyKey) return;
      setFinishedRunId("");
      setFinishedError("");
    }, [finishedBusyKey]);
    const archiveFinishedRun = useCallback(async () => {
      const run = finishedRun;
      if (!run || !run.id || !window.API.setActionCenterRunArchived) {
        setFinishedError("Action Center archive endpoint is not available.");
        return;
      }
      if (!run.threadId) {
        setFinishedError("This Action Center run does not have a persisted thread yet.");
        return;
      }
      try {
        setFinishedBusyKey("archive");
        setFinishedError("");
        await window.API.setActionCenterRunArchived(run.id, true);
        setFinishedRunId("");
        await load(true);
        if (onToast) onToast("Thread archived");
      } catch (error) {
        setFinishedError((error && error.message) || "Failed to archive thread.");
        if (onError) onError(error);
      } finally {
        setFinishedBusyKey("");
      }
    }, [finishedRun, load, onError, onToast]);
    const deleteFinishedRun = useCallback(async () => {
      const run = finishedRun;
      if (!run || !run.id || !window.API.deleteActionCenterRun) {
        setFinishedError("Action Center delete endpoint is not available.");
        return;
      }
      if (!window.confirm("Delete this thread? This removes it from the Action Center and queues Codex deletion when available.")) return;
      try {
        setFinishedBusyKey("delete");
        setFinishedError("");
        const result = await window.API.deleteActionCenterRun(run.id);
        setFinishedRunId("");
        await load(true);
        if (onToast) {
          const codexQueued = result && result.codexDeleteQueued;
          const stopped = result && result.activePromptStopped;
          const codexUnavailable = result && (result.codexDeleteError || result.stopError);
          onToast(codexQueued ? "Thread deleted; Codex deletion queued" : stopped ? "Thread deleted; active prompt stopped" : codexUnavailable ? "Thread deleted locally; Codex stop/delete unavailable" : "Thread deleted");
        }
      } catch (error) {
        setFinishedError((error && error.message) || "Failed to delete thread.");
        if (onError) onError(error);
      } finally {
        setFinishedBusyKey("");
      }
    }, [finishedRun, load, onError, onToast]);
    const openFinishedThread = useCallback(() => {
      const run = finishedRun;
      if (!run) return;
      setFinishedRunId("");
      setSelected({ type: "run", id: run.id });
      if (onOpenActionCenterRun) onOpenActionCenterRun(run.employeeId || run.agentId, run.id);
    }, [finishedRun, onOpenActionCenterRun]);
    const copyFinishedMessage = useCallback(async () => {
      if (finishedLoading || !String(finishedMessage || "").trim()) return;
      try {
        setFinishedBusyKey("copy");
        setFinishedError("");
        const copied = await copyMarkdownText(finishedMessage);
        if (!copied) throw new Error("Copy command was not accepted by the browser.");
        if (onToast) onToast("Final answer copied as Markdown");
      } catch (error) {
        setFinishedError((error && error.message) || "Failed to copy final answer.");
        if (onError) onError(error);
      } finally {
        setFinishedBusyKey("");
      }
    }, [finishedLoading, finishedMessage, onError, onToast]);
    const respondRunApproval = useCallback(async (run, requestId, decision) => {
      const jobId = approvalJobIdForRun(run);
      if (!jobId || !requestId || !window.API.respondCodexPromptApproval) {
        setRequestError("Approval endpoint is not available.");
        return;
      }
      try {
        setRequestBusy(true);
        setRequestError("");
        await window.API.respondCodexPromptApproval(jobId, requestId, decision);
        if (run && run.id) await loadRun(run.id);
        await load(true);
        if (run && sameId(requestRunId, run.id)) setRequestRunId("");
        if (onToast) onToast(structuredQuestionDecision(decision) ? "Answers submitted" : approvalToastForDecision(decision));
      } catch (error) {
        const message = (error && error.message) || "Request failed.";
        setRequestError(message);
        if (onError) onError(error);
      } finally {
        setRequestBusy(false);
      }
    }, [load, loadRun, onError, onToast, requestRunId]);
    async function respondRequestApproval(requestId, decision) {
      await respondRunApproval(requestRun, requestId, decision);
    }
    if (loading) return <div className="av-shell"><div className="ac-loading">Loading Activity...</div></div>;
    if (!agents.length) {
      return (
        <div className="av-shell">
          <div className="ac-empty-full">
            <Icon name="server" size={30} />
            <strong>No AI employees on shift</strong>
            <p>Create an active AI employee before assigning Action Center work.</p>
            <button className="btn" type="button" onClick={() => onNav && onNav("employees")}><Icon name="employees" size={14} />Open employees</button>
          </div>
        </div>
      );
    }

    const pendingItem = pendingAssignment ? backlogById[pendingAssignment.itemId] : null;
    const pendingAgent = pendingAssignment ? agents.find((agent) => agent.id === pendingAssignment.employeeId) : null;
    const pendingInstructions = pendingItem ? instructionsFor(pendingItem) : [];
    const customInstructionSelected = selectedInstructionId === CUSTOM_INSTRUCTION_ID;
    const chosenInstruction = customInstructionSelected ? null : (pendingInstructions.find((instruction) => instruction.id === selectedInstructionId) || pendingInstructions[0] || null);
    const canConfirmAssignment = customInstructionSelected ? !!customInstructionPrompt.trim() : !!chosenInstruction;
    const RequestCard = window.ActionCenterRequestCard;
    const resultRunLabel = "Finished result";

    return (
      <div className="av-shell">
        <header className="av-top">
          <ActionItemsButton />
          <div className="av-filters" role="group" aria-label="Filter runs">
            {[
              ["all", "All"],
              ["working", "Working"],
              ["needs-input", "Needs you"],
              ["review", "Finished"],
              ["done", "Done"],
              ["error", "Blocked"],
            ].map(([key, label]) => (
              <button key={key} type="button" className={`av-filter ${filter === key ? "on" : ""} ${statusMeta(key).cls}`} onClick={() => setFilter(key)}>
                <span className="av-filter-dot" />
                <span>{label}</span>
                <em>{counts[key]}</em>
              </button>
            ))}
          </div>
        </header>
        <div className="av-body">
          <main className="av-canvas" ref={canvasRef} onClick={(event) => { if (event.target === event.currentTarget) setSelected({ type: "overview", id: "" }); }}>
            <div className="av-world" style={{ width: layout.worldW, height: layout.worldH }}>
              <svg className="av-edges" width={layout.worldW} height={layout.worldH}>
                <defs>
                  {Object.keys(STATUS_META).map((key) => (
                    <marker key={key} id={`av-arrow-${key}`} viewBox="0 0 10 10" refX="8" refY="5" markerWidth="5" markerHeight="5" orient="auto-start-reverse">
                      <path d="M 0 0 L 10 5 L 0 10 z" fill={statusMeta(key).color} />
                    </marker>
                  ))}
                </defs>
                {edgeRuns.map((run) => {
                  const parent = runsById[runParentId(run)];
                  const from = layout.bubbles[parent.id];
                  const to = layout.bubbles[run.id];
                  if (!from || !to) return null;
                  const dx = to.x - from.x;
                  const dy = to.y - from.y;
                  const len = Math.max(1, Math.sqrt(dx * dx + dy * dy));
                  const cx = (from.x + to.x) / 2 - (dy / len) * len * 0.16;
                  const cy = (from.y + to.y) / 2 + (dx / len) * len * 0.16;
                  const edgeId = `${parent.id}>${run.id}`;
                  const meta = statusMeta(statusOf(run));
                  const markerKey = meta.cls === "needs" ? "needs-input" : (meta.cls === "working" ? "working" : statusOf(run));
                  const dim = shouldDimRun(run) || shouldDimRun(parent);
                  const delegationAnimation = delegationAnimations[edgeId] || "";
                  return (
                    <path
                      key={edgeId}
                      className={`av-edge ${["working", "needs-input", "waiting-delegates"].includes(statusOf(run)) ? "active" : ""} ${delegationAnimation ? `delegation-${delegationAnimation}` : ""} ${selectedEdge === edgeId ? "selected" : ""} ${dim ? "dim" : ""}`}
                      d={`M ${from.x} ${from.y} Q ${cx} ${cy} ${to.x} ${to.y}`}
                      stroke={dim ? "var(--line-2)" : meta.color}
                      markerEnd={`url(#av-arrow-${markerKey})`}
                      onClick={(event) => { event.stopPropagation(); setSelected({ type: "edge", id: edgeId }); }}
                    />
                  );
                })}
              </svg>
              {edgeRuns.map((run) => {
                const parent = runsById[runParentId(run)];
                const from = parent && layout.bubbles[parent.id];
                const to = layout.bubbles[run.id];
                if (!parent || !from || !to) return null;
                const edgeId = `${parent.id}>${run.id}`;
                const label = (layout.edgeLabels && layout.edgeLabels[edgeId]) || { x: (from.x + to.x) / 2, y: (from.y + to.y) / 2 - 24 };
                const delegationAnimation = delegationAnimations[edgeId] || "";
                return (
                  <button key={edgeId} type="button" className={`av-edge-label ${delegationAnimation ? `delegation-${delegationAnimation}` : ""} ${selectedEdge === edgeId ? "selected" : ""} ${(shouldDimRun(run) || shouldDimRun(parent)) ? "dim" : ""}`} style={{ left: label.x, top: label.y }} onClick={(event) => { event.stopPropagation(); setSelected({ type: "edge", id: edgeId }); }}>
                    <Icon name="handover" size={12} />
                    <span>{handoffLabel(run)}</span>
                  </button>
                );
              })}
              {agents.map((agent) => {
                const pos = layout.positions[agent.id];
                if (!pos) return null;
                const agentRuns = runsByAgent[agent.id] || [];
                const needs = agentRuns.filter((run) => statusOf(run) === "needs-input").length;
                const activeCount = agentRuns.filter((run) => statusOf(run) !== "done").length;
                const finishedCount = agentRuns.length - activeCount;
                const runSummary = [
                  activeCount ? `${activeCount} active` : "",
                  finishedCount ? `${finishedCount} finished` : "",
                  needs ? `${needs} need you` : "",
                ].filter(Boolean).join(" · ") || "Available";
                const dim = filter !== "all" && !agentRuns.some((run) => filteredRuns.includes(run));
                return (
                  <div
                    key={agent.id}
                    className={`av-agent ${agentRuns.length ? "" : "idle"} ${selected.type === "employee" && selected.id === agent.id ? "selected" : ""} ${dropAgentId === agent.id ? "drop" : ""} ${dim ? "dim" : ""}`}
                    style={{ left: pos.x, top: pos.y, "--agent-color": employeeColor(agent) }}
                    onDragOver={(event) => { event.preventDefault(); setDropAgentId(agent.id); }}
                    onDragLeave={() => setDropAgentId("")}
                    onDrop={(event) => {
                      event.preventDefault();
                      const itemId = event.dataTransfer.getData("text/plain") || dragItemId;
                      setDropAgentId("");
                      setDragItemId("");
                      assign(itemId, agent.id);
                    }}
                  >
                    <button type="button" className="av-agent-mainbutton" onClick={(event) => { event.stopPropagation(); setSelected({ type: "employee", id: agent.id }); }}>
                      {employeeAvatar(agent, "node")}
                      <span className="av-agent-caption">
                        <strong>{agent.name}</strong>
                        <span>{agent.role || "AI employee"}</span>
                        <em className={needs ? "needs" : ""}>{runSummary}</em>
                      </span>
                    </button>
                    <button type="button" className="av-agent-archive" title={`Archived threads for ${agent.name}`} aria-label={`Archived threads for ${agent.name}`} onClick={(event) => { event.stopPropagation(); openArchivedThreadsMenu(agent, event); }}>
                      <Icon name="archive" size={14} />
                    </button>
                    <button type="button" className="av-agent-plus" title={`Start a new thread with ${agent.name}`} aria-label={`Start a new thread with ${agent.name}`} onClick={(event) => { event.stopPropagation(); openStandardTaskMenu(agent, event); }}>
                      <Icon name="plus" size={15} />
                    </button>
                  </div>
                );
              })}
              {runs.map((run) => {
                const pos = layout.bubbles[run.id];
                if (!pos) return null;
                const selectedBubble = selected.type === "run" && selected.id === run.id;
                return (
                  <RunNode
                    key={run.id}
                    run={run}
                    agent={agentsById[run.employeeId || run.agentId]}
                    position={pos}
                    selected={selectedBubble}
                    dim={shouldDimRun(run)}
                    activityOverride={bridgeActivityByRunId && bridgeActivityByRunId[String(run.id || "").toLowerCase()]}
                    speechOverride={bridgeSpeechByRunId && bridgeSpeechByRunId[String(run.id || "").toLowerCase()]}
                    requestBusy={requestBusy}
                    onSelectRun={selectRun}
                    requestSelected={requestRunId && sameId(requestRunId, run.id)}
                    onOpenRequest={openRequest}
                    finishedSelected={finishedRunId && sameId(finishedRunId, run.id)}
                    onOpenFinished={openFinished}
                    onRespondApproval={respondRunApproval}
                    db={db}
                  />
                );
              })}
            </div>
            <ActionItemsDrawer />
          </main>
        </div>
        {standardTaskMenu && (
          <div className="ac-standard-task-menu" style={{ left: standardTaskMenu.x, top: standardTaskMenu.y }} onMouseDown={(event) => event.stopPropagation()}>
            {standardTaskMenu.mode === "tasks" ? (
              <div className="ac-standard-task-list">
                {standardTasksFor(standardTaskMenu.employee).map((task) => (
                  <button
                    key={task.id || task.title}
                    type="button"
                    className="ac-standard-task"
                    disabled={!!busyKey}
                    onClick={() => {
                      if (task.scopeType) setStandardTaskMenu({ ...standardTaskMenu, mode: "scope", task });
                      else if (standardTaskAllowsInput(task)) setStandardTaskMenu({ ...standardTaskMenu, mode: "input", task, entity: null, workPackage: null, input: "" });
                      else submitStandardTask(standardTaskMenu.employee, task, null);
                    }}
                  >
                    <span><strong>{task.title}</strong><em>{standardTaskActionLabel(task)}</em></span>
                    <Icon name={task.scopeType || standardTaskAllowsInput(task) ? "chevR" : "send"} size={13} />
                  </button>
                ))}
                <button type="button" className="ac-standard-task custom" onClick={() => { setStandardTaskMenu(null); openStartComposer(standardTaskMenu.employee); }}>
                  <span><strong>Custom prompt</strong><em>Write a one-off task</em></span>
                  <Icon name="edit" size={13} />
                </button>
              </div>
            ) : standardTaskMenu.mode === "scope" ? (
              <div className="ac-standard-task-list scoped">
                <button type="button" className="ac-standard-task back" onClick={() => setStandardTaskMenu({ ...standardTaskMenu, mode: "tasks", task: null })}>
                  <Icon name="chevL" size={13} />
                  <span><strong>Back</strong><em>{standardTaskMenu.task.title}</em></span>
                </button>
                {scopedEntities(standardTaskMenu.task.scopeType).map((entity) => (
                  <button
                    key={entity.id}
                    type="button"
                    className="ac-standard-task"
                    disabled={!!busyKey}
                    onClick={() => {
                      if (standardTaskRequiresWorkPackage(standardTaskMenu.task)) setStandardTaskMenu({ ...standardTaskMenu, mode: "workPackage", entity });
                      else if (standardTaskAllowsInput(standardTaskMenu.task)) setStandardTaskMenu({ ...standardTaskMenu, mode: "input", entity, workPackage: null, input: "" });
                      else submitStandardTask(standardTaskMenu.employee, standardTaskMenu.task, entity);
                    }}
                  >
                    <span><strong>{entity.name || "Untitled"}</strong><em>{standardTaskMenu.task.title}</em></span>
                    <Icon name={standardTaskRequiresWorkPackage(standardTaskMenu.task) || standardTaskAllowsInput(standardTaskMenu.task) ? "chevR" : "send"} size={13} />
                  </button>
                ))}
                {scopedEntities(standardTaskMenu.task.scopeType).length === 0 && <div className="ac-empty">No {standardTaskMenu.task.scopeType.toLowerCase()}s available.</div>}
              </div>
            ) : standardTaskMenu.mode === "workPackage" ? (
              <div className="ac-standard-task-list scoped">
                <button type="button" className="ac-standard-task back" onClick={() => setStandardTaskMenu({ ...standardTaskMenu, mode: "scope", entity: null, workPackage: null })}>
                  <Icon name="chevL" size={13} />
                  <span><strong>Back</strong><em>{standardTaskMenu.entity && standardTaskMenu.entity.name}</em></span>
                </button>
                {workPackagesForProject(standardTaskMenu.entity).map((workPackage) => (
                  <button
                    key={workPackage.id}
                    type="button"
                    className="ac-standard-task"
                    disabled={!!busyKey}
                    onClick={() => {
                      if (standardTaskAllowsInput(standardTaskMenu.task)) setStandardTaskMenu({ ...standardTaskMenu, mode: "input", workPackage, input: "" });
                      else submitStandardTask(standardTaskMenu.employee, standardTaskMenu.task, standardTaskMenu.entity, { workPackage });
                    }}
                  >
                    <span><strong>{workPackage.name || "Work package"}</strong><em>{standardTaskMenu.entity && standardTaskMenu.entity.name}</em></span>
                    <Icon name={standardTaskAllowsInput(standardTaskMenu.task) ? "chevR" : "send"} size={13} />
                  </button>
                ))}
                {workPackagesForProject(standardTaskMenu.entity).length === 0 && <div className="ac-empty">No work packages available.</div>}
              </div>
            ) : (
              <form className="ac-standard-task-input" onSubmit={(event) => { event.preventDefault(); submitStandardTask(standardTaskMenu.employee, standardTaskMenu.task, standardTaskMenu.entity, { workPackage: standardTaskMenu.workPackage, additionalInput: standardTaskMenu.input }); }}>
                <button
                  type="button"
                  className="ac-standard-task back"
                  onClick={() => setStandardTaskMenu({
                    ...standardTaskMenu,
                    mode: standardTaskMenu.workPackage ? "workPackage" : standardTaskMenu.task && standardTaskMenu.task.scopeType ? "scope" : "tasks",
                    input: "",
                  })}
                >
                  <Icon name="chevL" size={13} />
                  <span><strong>Back</strong><em>{standardTaskMenu.task.title}</em></span>
                </button>
                <label>
                  <span>Custom prompt</span>
                  <MentionTextarea
                    className="inp"
                    rows={10}
                    value={standardTaskMenu.input || ""}
                    mentionOptions={mentionOptions}
                    onValueChange={(nextValue) => setStandardTaskMenu({ ...standardTaskMenu, input: nextValue })}
                    onKeyDown={(event) => submitPromptOnEnter(event, () => submitStandardTask(standardTaskMenu.employee, standardTaskMenu.task, standardTaskMenu.entity, { workPackage: standardTaskMenu.workPackage, additionalInput: standardTaskMenu.input }))}
                    placeholder="Add optional instructions for this run..."
                    autoFocus
                    data-appligator-id="standard-task-input-mentions"
                  />
                </label>
                <button type="submit" className="btn sm primary" disabled={!!busyKey}><Icon name="send" size={13} />Start task</button>
              </form>
            )}
          </div>
        )}
        {archivedMenu && (
          <div className="ac-archived-thread-menu" style={{ left: archivedMenu.x, top: archivedMenu.y }} onMouseDown={(event) => event.stopPropagation()}>
            <header>
              <div>
                <strong>Archived threads</strong>
                <span>{archivedMenu.employee ? archivedMenu.employee.name : "AI employee"}</span>
              </div>
              <button type="button" className="iconbtn" title="Close" onClick={() => setArchivedMenu(null)}><Icon name="close" size={14} /></button>
            </header>
            <label className="ac-archived-search">
              <Icon name="search" size={13} />
              <input value={archivedQuery} onChange={(event) => setArchivedQuery(event.target.value)} placeholder="Search archived threads..." autoFocus />
            </label>
            <div className="ac-archived-thread-list">
              {archivedMenu.loading && <div className="ac-empty">Loading archived threads...</div>}
              {!archivedMenu.loading && archivedMenu.error && <div className="ac-empty">{archivedMenu.error}</div>}
              {!archivedMenu.loading && !archivedMenu.error && archivedThreads.map((thread) => {
                const title = thread.title || thread.preview || thread.contextScopeLabel || "Archived thread";
                const meta = [thread.projectName || thread.customerName || thread.contextScopeLabel, dateTimeLabel(thread.updatedAt)].filter(Boolean).join(" - ");
                return (
                  <button
                    key={thread.threadId}
                    type="button"
                    className="ac-archived-thread"
                    disabled={!thread.runId}
                    onClick={() => {
                      if (!thread.runId) return;
                      setArchivedMenu(null);
                      setSelected({ type: "run", id: thread.runId });
                      loadRun(thread.runId);
                      if (onOpenActionCenterRun) onOpenActionCenterRun(thread.employeeId, thread.runId);
                    }}
                  >
                    <span>
                      <strong>{title}</strong>
                      <em>{meta || thread.threadId}</em>
                    </span>
                    <Icon name={thread.runId ? "chevR" : "unlink"} size={13} />
                  </button>
                );
              })}
              {!archivedMenu.loading && !archivedMenu.error && archivedThreads.length === 0 && <div className="ac-empty">{archivedQuery.trim() ? "No archived threads match this search." : "No archived threads."}</div>}
            </div>
          </div>
        )}
        {requestRunId && (
          <div className="ac-request-overlay" onMouseDown={(event) => { if (event.target === event.currentTarget && !requestBusy) setRequestRunId(""); }}>
            <section className="ac-request-dialog" role="dialog" aria-modal="true" aria-label="Action Center request" onMouseDown={(event) => event.stopPropagation()}>
              <button type="button" className="ac-request-close" title="Close" aria-label="Close request" disabled={!!requestBusy} onClick={() => setRequestRunId("")}>
                <Icon name="close" size={14} />
              </button>
              {requestApproval && RequestCard ? (
                <RequestCard approvalRequest={requestApproval} busy={requestBusy} onRespondApproval={respondRequestApproval} className="ac-request-card" />
              ) : (
                <div className="ac-request-empty">
                  <Icon name="alert" size={16} />
                  <span>{requestRun ? "This request is no longer pending." : "This thread is no longer active."}</span>
                </div>
              )}
              {requestError && <div className="ac-request-error"><Icon name="alert" size={13} />{requestError}</div>}
            </section>
          </div>
        )}
        {finishedRunId && (
          <div className="ac-finished-overlay" onMouseDown={(event) => { if (event.target === event.currentTarget) closeFinished(); }}>
            <section className="ac-finished-dialog" role="dialog" aria-modal="true" aria-label={resultRunLabel} onMouseDown={(event) => event.stopPropagation()}>
              <header className="ac-finished-head">
                <div>
                  <strong>{finishedRun ? finishedRun.title || resultRunLabel : resultRunLabel}</strong>
                  <span>{finishedRun ? [finishedRun.contextScopeLabel, finishedRun.threadId].filter(Boolean).join(" - ") : "Loading final message..."}</span>
                </div>
                <button type="button" className="ac-request-close" title="Close" aria-label="Close finished result" disabled={!!finishedBusyKey} onClick={closeFinished}>
                  <Icon name="close" size={14} />
                </button>
              </header>
              <div className="ac-finished-body">
                {finishedLoading ? (
                  <div className="ac-request-empty"><Icon name="server" size={16} /><span>Loading final message...</span></div>
                ) : (
                  <MarkdownText text={finishedMessage} />
                )}
                {finishedError && <div className="ac-request-error"><Icon name="alert" size={13} />{finishedError}</div>}
              </div>
              <footer className="ac-finished-actions">
                <button type="button" className="primary" disabled={!!finishedBusyKey || finishedLoading || !String(finishedMessage || "").trim()} onClick={copyFinishedMessage}>
                  <Icon name="copy" size={14} />
                  <span>{finishedBusyKey === "copy" ? "Copying..." : "Copy Markdown"}</span>
                </button>
                <button type="button" disabled={!!finishedBusyKey || !finishedRun || !finishedRun.threadId} onClick={archiveFinishedRun}>
                  <Icon name="archive" size={14} />
                  <span>{finishedBusyKey === "archive" ? "Archiving..." : "Archive"}</span>
                </button>
                <button type="button" className="danger" disabled={!!finishedBusyKey || !finishedRun} onClick={deleteFinishedRun}>
                  <Icon name="trash" size={14} />
                  <span>{finishedBusyKey === "delete" ? "Deleting..." : "Delete"}</span>
                </button>
                <button type="button" disabled={!!finishedBusyKey || !finishedRun} onClick={openFinishedThread}>
                  <Icon name="handover" size={14} />
                  <span>Open thread</span>
                </button>
              </footer>
            </section>
          </div>
        )}
        {pendingAssignment && (
          <div className="ac-modal-overlay" onMouseDown={(event) => { if (event.target === event.currentTarget) setPendingAssignment(null); }}>
            <section className="ac-assign-modal" role="dialog" aria-modal="true" aria-labelledby="ac-assign-title">
              <header className="ac-assign-head">
                <div>
                  <h2 id="ac-assign-title">Assign task</h2>
                  <p>{pendingItem ? pendingItem.title : "Action item"} · {pendingAgent ? pendingAgent.name : "AI employee"}</p>
                </div>
                <button className="iconbtn" type="button" onClick={() => setPendingAssignment(null)} aria-label="Close"><Icon name="close" size={16} /></button>
              </header>
              <div className="ac-instruction-list">
                {pendingInstructions.length === 0 && <div className="ac-empty">No instructions are configured for this action item type.</div>}
                {pendingInstructions.map((instruction) => (
                  <button key={instruction.id} type="button" className={`ac-instruction-option ${instruction.id === (chosenInstruction && chosenInstruction.id) ? "selected" : ""}`} onClick={() => setSelectedInstructionId(instruction.id)}>
                    <span className="ac-instruction-radio" />
                    <span><strong>{instruction.title}</strong><em>{instruction.description}</em></span>
                  </button>
                ))}
                <button type="button" className={`ac-instruction-option ${customInstructionSelected ? "selected" : ""}`} onClick={() => setSelectedInstructionId(CUSTOM_INSTRUCTION_ID)}>
                  <span className="ac-instruction-radio" />
                  <span><strong>Custom command</strong><em>Write a one-off prompt for this assignment.</em></span>
                </button>
              </div>
              {customInstructionSelected && (
                <div className="ac-custom-command">
                  <label htmlFor="ac-custom-command">Custom prompt</label>
                  <textarea id="ac-custom-command" rows="6" value={customInstructionPrompt} placeholder={`Tell ${pendingAgent ? firstName(pendingAgent.name) : "the AI employee"} what to do with this task...`} onChange={(event) => setCustomInstructionPrompt(event.target.value)} />
                  <small>The final prompt will include the selected AI employee, task metadata, and Action Center guardrails.</small>
                </div>
              )}
              {chosenInstruction && <div className="ac-instruction-prompt"><strong>Prompt behind this instruction</strong><p>{chosenInstruction.prompt}</p></div>}
              <footer className="ac-assign-actions">
                <button className="btn ghost" type="button" onClick={() => setPendingAssignment(null)}>Cancel</button>
                <button className="btn" type="button" disabled={!canConfirmAssignment || !!busyKey} onClick={confirmAssignment}><Icon name="handover" size={14} />Assign</button>
              </footer>
            </section>
          </div>
        )}
        {compose && (
          <div className="ac-modal-overlay" onMouseDown={(event) => { if (event.target === event.currentTarget && !busyKey) setCompose(null); }}>
            <section className="ai-compose-modal ac-start-composer" role="dialog" aria-modal="true" aria-labelledby="av-compose-title">
              <header>
                {employeeAvatar(compose.employee, "md")}
                <div>
                  <h2 id="av-compose-title">Message {compose.employee.name}</h2>
                  <p>{compose.reference.type} · {compose.reference.label}</p>
                </div>
                <button className="icon-btn" type="button" title="Close" onClick={() => !busyKey && setCompose(null)} disabled={!!busyKey}><Icon name="close" size={16} /></button>
              </header>
              <div className="body">
                <div className="ai-compose-reference">
                  <span>{compose.reference.type}</span>
                  <strong>{compose.reference.label}</strong>
                </div>
                <div className="field">
                  <label>Prompt</label>
                  <MentionTextarea
                    className="inp ai-compose-textarea"
                    rows={10}
                    value={composePrompt}
                    mentionOptions={mentionOptions}
                    onValueChange={setComposePrompt}
                    onKeyDown={(event) => submitPromptOnEnter(event, submitStartComposer)}
                    placeholder="What should this employee do?"
                    autoFocus
                    data-appligator-id="action-center-start-composer-mentions"
                  />
                </div>
              </div>
              <footer>
                <button className="btn ghost" type="button" onClick={() => setCompose(null)} disabled={!!busyKey}>Cancel</button>
                <button className="btn" type="button" onClick={submitStartComposer} disabled={!!busyKey || !composePrompt.trim()}><Icon name="send" size={14} />{busyKey ? "Sending..." : "Send"}</button>
              </footer>
            </section>
          </div>
        )}
      </div>
    );

    function shouldDimRun(run) {
      if (!run) return false;
      const filterDim = filter !== "all" && !(filter === "error" ? statusOf(run) === "error" : statusOf(run) === filter);
      const emphasisDim = emphasis && !emphasis.has(run.id);
      return filterDim || emphasisDim;
    }
    function ActionItemsButton() {
      return (
        <button type="button" className={`av-items-header ${itemsOpen ? "on" : ""}`} onClick={() => setItemsOpen((open) => !open)}>
          <Icon name="flag" size={14} />
          <span>Action items</span>
          <em>{searchedBacklog.length}</em>
          <Icon name={itemsOpen ? "chevL" : "chevR"} size={13} />
        </button>
      );
    }
    function ActionItemsDrawer() {
      if (!itemsOpen) return null;
      return (
        <section className="av-items-panel">
          <header>
            <div><strong>Action items</strong><span>{searchedBacklog.length} unassigned · drag onto anyone</span></div>
            <button type="button" className="iconbtn" onClick={() => setItemsOpen(false)} aria-label="Collapse action items"><Icon name="chevL" size={15} /></button>
          </header>
          <div className="av-items-list">
            {searchedBacklog.length === 0 && <div className="ac-empty">Everything is assigned.</div>}
            {searchedBacklog.map((item) => {
              const meta = sourceMeta(item.type);
              const suggested = agents.find((agent) => agent.id === item.suggestedEmployeeId) || agents[0];
              return (
                <article
                  key={item.id}
                  className={`av-action-card ${dragItemId === item.id ? "dragging" : ""}`}
                  draggable
                  onDragStart={(event) => {
                    setDragItemId(item.id);
                    event.dataTransfer.effectAllowed = "move";
                    event.dataTransfer.setData("text/plain", item.id);
                  }}
                  onDragEnd={() => { setDragItemId(""); setDropAgentId(""); }}
                >
                  <div className="av-action-source">
                    <span><Icon name={meta.icon} size={13} /></span>
                    <em>{meta.label}</em>
                    <strong className={priorityClass(item.priority)}>{priorityLabel(item.priority)}</strong>
                  </div>
                  <h3>{item.title || "Untitled action item"}</h3>
                  <p>{item.projectName || item.customerName || meta.label}</p>
                  {suggested && (
                    <button className="av-suggest" type="button" disabled={!!busyKey} onClick={() => assign(item.id, suggested.id)}>
                      <span>Suggest</span>
                      {employeeAvatar(suggested, "xs")}
                      <strong>{firstName(suggested.name)}</strong>
                    </button>
                  )}
                </article>
              );
            })}
          </div>
        </section>
      );
    }
  }

  const DOMAIN_COLORS = {
    tickets: "#0e7490",
    work: "#2856c4",
    codex: "#0f1b2d",
    knowledge: "#6d4bd0",
    architecture: "#1d4ed8",
    documents: "#64748b",
    guidelines: "#9d2c5b",
    design: "#b45309",
    delivery: "#15803d",
    finance: "#7c5800",
    delegation: "#7c3aed",
    action: "#2856c4",
    tests: "#b45309",
    email: "#0e7490",
    meetings: "#64748b",
    notes: "#64748b",
    projects: "#1d4ed8",
    directory: "#15803d",
    default: "#475569",
  };

  function runTitle(run) {
    return String(run && (run.threadTitle || run.title || run.contextScopeLabel) || "Untitled thread").trim();
  }
  function employeeIdForRun(run) {
    return run && (run.employeeId || run.agentId || run.agent || "");
  }
  function activeRunStatus(run) {
    return ["queued", "working", "waiting-delegates", "needs-input", "review", "error"].includes(statusOf(run));
  }
  function domainColor(domainKey) {
    return DOMAIN_COLORS[domainKey] || DOMAIN_COLORS.default;
  }
  function speechTextFromActivity(activity) {
    return window.ActionCenterActivity ? window.ActionCenterActivity.speechTextFromActivity(activity) : "";
  }
  function speechMetaFromActivity(activity) {
    return window.ActionCenterActivity ? window.ActionCenterActivity.speechMetaFromActivity(activity) : null;
  }
  function statusFilterMatch(run, filter) {
    if (filter === "all") return true;
    const status = statusOf(run);
    if (filter === "working") return status === "working" || status === "waiting-delegates" || status === "queued";
    return status === filter;
  }

  function StatusPill({ run }) {
    const meta = statusMeta(statusOf(run));
    return (
      <span className={`avc-status-pill ${meta.cls}`} style={{ "--status-color": meta.color, "--status-soft": meta.soft }}>
        {meta.label}
      </span>
    );
  }

  function StatusBadge({ run }) {
    const meta = statusMeta(statusOf(run));
    return (
      <span className="avc-status-badge" style={{ "--status-color": meta.color }}>
        <Icon name={runStatusIcon(run) || "clock"} size={10} />
      </span>
    );
  }

  function ToolChip({ activity, db, exiting = false }) {
    const path = toolActivityPath(activity, db);
    if (!path) return null;
    const color = domainColor(path.domainKey);
    const actionNode = path.nodes[path.nodes.length - 1] || { label: "Work" };
    const sectionNode = path.nodes.find((node) => node.tone === "section") || path.nodes[1] || { label: "Tool" };
    const subjectNode = path.nodes.find((node) => node.tone === "subject");
    const code = path.domainKey === "codex" ? "</>" : String(sectionNode.label || path.domainKey || "TL").slice(0, 2).toUpperCase();
    return (
      <div className={`avc-tool-chip ${exiting ? "exiting" : ""}`} style={{ "--tool-color": color, "--tool-soft": color }}>
        <span className="avc-tool-code">{code}</span>
        <span className="avc-tool-copy">
          <strong>{actionNode.label} <em>· {sectionNode.label}</em></strong>
          <small>{subjectNode ? subjectNode.label : path.title}</small>
        </span>
      </div>
    );
  }

  function ActivityZone({ run, activityOverride, db }) {
    const eventActivity = useRunBridgeActivity(run && run.id);
    const activity = activityOverride || eventActivity;
    const toolEntries = useRunToolActivityStream(run && run.id, activityOverride, db);
    if (!toolEntries.length) {
      return <div className="avc-idle-line">{statusOf(run) === "review" ? "Finished" : statusOf(run) === "needs-input" ? "Waiting for your answer" : statusOf(run) === "done" ? "Completed" : "Working..."}</div>;
    }
    return (
      <div className="avc-activity-stack">
        <div className="avc-tools">
          {toolEntries.slice(0, 3).map((entry) => (
            <ToolChip key={entry.key} activity={entry.activity} db={db} exiting={entry.exiting} />
          ))}
        </div>
      </div>
    );
  }

  function RunCard({ run, agent, parent, parentAgent, threadCount, selected, dim, activityOverride, speechOverride, requestBusy, onSelectRun, onRespondApproval, db }) {
    const meta = statusMeta(statusOf(run));
    const pct = displayPct(run);
    const hasSpeech = !!speechTextFromActivity(speechOverride);
    const approvalRequest = statusOf(run) === "done" ? null : approvalRequestFromRun(run);
    const hasRequest = !!approvalRequest;
    return (
      <div className={`avc-node-wrap ${hasSpeech || hasRequest ? "has-side-content" : ""}`}>
        <button
          type="button"
          className={`avc-node-card ${selected ? "selected" : ""} ${dim ? "dim" : ""}`}
          style={{ "--status-color": meta.color, "--status-soft": meta.soft }}
          onClick={() => onSelectRun(run)}
        >
          <header className="avc-node-head">
            <span className="avc-avatar-wrap">
              {employeeAvatar(agent || { name: "AI" }, "thread")}
              <StatusBadge run={run} />
            </span>
            <span className="avc-node-person">
              <strong>{agent ? agent.name : "AI employee"}</strong>
              <em>{agent && agent.role ? agent.role : "AI employee"}</em>
            </span>
            {threadCount > 1 && <span className="avc-thread-count"><Icon name="stack" size={11} />x{threadCount}</span>}
          </header>
          {parent && (
            <div className="avc-from-pill">
              <Icon name="handover" size={10} />
              <span>From {firstName(parentAgent && parentAgent.name)}</span>
            </div>
          )}
          <div className="avc-node-status">
            <StatusPill run={run} />
            <span className="mono">{pct}%</span>
          </div>
          <h3>{runTitle(run)}</h3>
          <div className="avc-progress"><span style={{ width: `${pct}%` }} /></div>
          <ActivityZone run={run} activityOverride={activityOverride} db={db} />
        </button>
        <RunSpeechBubble activity={speechOverride} statusColor={meta.color} statusSoft={meta.soft} />
        <RunInlineRequestCard run={run} approvalRequest={approvalRequest} busy={requestBusy} onRespondApproval={onRespondApproval} className="avc-card-request-card" />
      </div>
    );
  }

  function ReviewQueue({ children, agentsById, selectedRunId, dim, onSelectRun }) {
    if (!children.length) return null;
    const height = 44 + Math.min(children.length, 6) * 28 + 12;
    return (
      <section className={`avc-review-queue ${dim ? "dim" : ""}`} style={{ height }}>
        <header>
          <span><Icon name="inbox" size={14} />Finished</span>
          <em className="mono">{children.length}</em>
        </header>
        <div className="avc-review-list">
          {children.map((run) => {
            const agent = agentsById[employeeIdForRun(run)];
            return (
              <button key={run.id} type="button" className={sameId(selectedRunId, run.id) ? "selected" : ""} onClick={() => onSelectRun(run)}>
                {employeeAvatar(agent || { name: "AI" }, "xs")}
                <span><strong>{runTitle(run)}</strong><em>{agent ? agent.name : "AI employee"}</em></span>
                <i />
              </button>
            );
          })}
        </div>
      </section>
    );
  }

  function Fan({ parent, children, reviewChildren, dim, delegationAnimations }) {
    const rows = children.length + (reviewChildren.length ? 1 : 0);
    if (!rows) return null;
    const rowGap = 26;
    const cardH = 252;
    const queueH = reviewChildren.length ? 44 + Math.min(reviewChildren.length, 6) * 28 + 12 : 0;
    const childHeights = [...children.map(() => cardH), ...(reviewChildren.length ? [queueH] : [])];
    const totalH = childHeights.reduce((sum, h) => sum + h, 0) + rowGap * Math.max(0, rows - 1);
    const rootCy = totalH / 2;
    let cursor = 0;
    return (
      <div className={`avc-fan ${dim ? "dim" : ""}`} style={{ height: totalH }}>
        <svg width="92" height={totalH} aria-hidden="true">
          {childHeights.map((h, index) => {
            const childCy = cursor + h / 2;
            cursor += h + rowGap;
            const review = index >= children.length;
            const child = review ? null : children[index];
            const edgeId = child ? `${parent.id}>${child.id}` : `${parent.id}>review`;
            const d = `M0 ${rootCy} C50.6 ${rootCy},41.4 ${childCy},92 ${childCy}`;
            const animated = child && delegationAnimations && delegationAnimations[edgeId];
            return (
              <g key={edgeId}>
                <path className={`avc-fan-path ${review ? "review" : ""} ${animated ? "pulse" : ""}`} d={d} />
                {animated && <circle className="avc-fan-dot" r="6"><animateMotion dur="2.2s" repeatCount="indefinite" path={d} /></circle>}
              </g>
            );
          })}
        </svg>
        <span>Delegates</span>
      </div>
    );
  }

  function TreeNode({ run, childrenByParent, agentsById, runsById, threadCounts, selectedRunId, filter, delegationAnimations, busyKey, bridgeActivityByRunId, bridgeSpeechByRunId, onSelectRun, onRespondApproval, db }) {
    const children = (childrenByParent[run.id] || []).slice().sort(runSort);
    const reviewChildren = children.filter((child) => statusOf(child) === "review");
    const activeChildren = children.filter((child) => statusOf(child) !== "review");
    const agent = agentsById[employeeIdForRun(run)];
    const parent = runParentId(run) ? runsById[runParentId(run)] : null;
    const parentAgent = parent ? agentsById[employeeIdForRun(parent)] : null;
    const dim = !statusFilterMatch(run, filter);
    return (
      <div className="avc-tree-node">
        <RunCard
          run={run}
          agent={agent}
          parent={parent}
          parentAgent={parentAgent}
          threadCount={threadCounts[employeeIdForRun(run)] || 0}
          selected={sameId(selectedRunId, run.id)}
          dim={dim}
          activityOverride={bridgeActivityByRunId && bridgeActivityByRunId[String(run.id || "").toLowerCase()]}
          speechOverride={bridgeSpeechByRunId && bridgeSpeechByRunId[String(run.id || "").toLowerCase()]}
          requestBusy={!!busyKey}
          onSelectRun={onSelectRun}
          onRespondApproval={onRespondApproval}
          db={db}
        />
        {(activeChildren.length || reviewChildren.length) ? (
          <>
            <Fan parent={run} children={activeChildren} reviewChildren={reviewChildren} dim={dim} delegationAnimations={delegationAnimations} />
            <div className="avc-children">
              {activeChildren.map((child) => (
                <TreeNode
                  key={child.id}
                  run={child}
                  childrenByParent={childrenByParent}
                  agentsById={agentsById}
                  runsById={runsById}
                  threadCounts={threadCounts}
                  selectedRunId={selectedRunId}
                  filter={filter}
                  delegationAnimations={delegationAnimations}
                  busyKey={busyKey}
                  bridgeActivityByRunId={bridgeActivityByRunId}
                  bridgeSpeechByRunId={bridgeSpeechByRunId}
                  onSelectRun={onSelectRun}
                  onRespondApproval={onRespondApproval}
                  db={db}
                />
              ))}
              <ReviewQueue children={reviewChildren} agentsById={agentsById} selectedRunId={selectedRunId} dim={dim} onSelectRun={onSelectRun} />
            </div>
          </>
        ) : null}
      </div>
    );
  }

  function drawerMessageKey(message, index) {
    return (message && (message.id || message.Id)) || `${(message && (message.kind || message.Kind)) || "message"}-${index}`;
  }
  function drawerMessageText(message) {
    return String((message && (message.text ?? message.Text ?? message.body ?? message.Body)) || "").trim();
  }
  function drawerMessageTime(message) {
    return dateTimeLabel(message && (message.createdAt || message.CreatedAt || message.timestamp || message.Timestamp));
  }
  function drawerMessageMetadata(message) {
    const raw = message && (message.metadataJson || message.MetadataJson);
    if (!raw) return {};
    try {
      const parsed = JSON.parse(String(raw || ""));
      return parsed && typeof parsed === "object" ? parsed : {};
    } catch {
      return {};
    }
  }
  function drawerMessageRequestId(message) {
    const metadata = drawerMessageMetadata(message);
    return String((message && (message.requestId || message.RequestId)) || metadata.requestId || metadata.RequestId || "").trim();
  }
  function pendingDrawerQuestionText(messages, approvalRequest) {
    if (!approvalRequest || !approvalRequest.requestId) return "";
    for (let index = (messages || []).length - 1; index >= 0; index -= 1) {
      const message = messages[index];
      if (norm(message && (message.kind || message.Kind)) !== "question") continue;
      const requestId = drawerMessageRequestId(message);
      if (!requestId || requestId === approvalRequest.requestId) return drawerMessageText(message);
    }
    return "";
  }
  function DrawerApprovalFallback({ approvalRequest, body, busy, onRespondApproval }) {
    if (!approvalRequest) return null;
    const respond = (decision) => {
      if (!onRespondApproval || busy) return;
      onRespondApproval(approvalRequest.requestId, decision);
    };
    return (
      <div className="avc-thread-question">
        <div><Icon name="alert" size={12} />Needs your input</div>
        <MarkdownText text={body || approvalRequestPreview(approvalRequest)} />
        {approvalRequest.requestId && (
          <div className="avc-thread-approval-actions">
            <button type="button" disabled={!!busy} onClick={() => respond("approve")}>Approve</button>
            <button type="button" disabled={!!busy} onClick={() => respond("decline")}>Decline</button>
          </div>
        )}
      </div>
    );
  }
  function DrawerApprovalCard({ approvalRequest, body, busy, onRespondApproval }) {
    if (!approvalRequest) return null;
    const RequestCard = window.ActionCenterRequestCard;
    if (!RequestCard) return <DrawerApprovalFallback approvalRequest={approvalRequest} body={body} busy={busy} onRespondApproval={onRespondApproval} />;
    return (
      <div className="avc-thread-question-card">
        <RequestCard approvalRequest={approvalRequest} body={body} busy={busy} onRespondApproval={onRespondApproval} className="avc-thread-request-card" />
      </div>
    );
  }
  function DrawerApprovalPanel({ approvalRequest, messages, busy, onRespondApproval }) {
    if (!approvalRequest) return null;
    const body = pendingDrawerQuestionText(messages, approvalRequest) || approvalRequestPreview(approvalRequest);
    return (
      <aside className="avc-approval-flyout" aria-label="Pending approval request" data-appligator-id="action-center-drawer-approval-flyout">
        <header>
          <span><Icon name="alert" size={13} />Pending request</span>
          <strong>{approvalRequest.title || "Approval required"}</strong>
        </header>
        <DrawerApprovalCard approvalRequest={approvalRequest} body={body} busy={busy} onRespondApproval={onRespondApproval} />
      </aside>
    );
  }
  function DrawerMessage({ message, employee, index }) {
    const kind = norm(message && (message.kind || message.Kind));
    const role = norm(message && (message.role || message.Role));
    const text = drawerMessageText(message);
    const time = drawerMessageTime(message);
    if (!text) return null;
    if (kind === "step") {
      return (
        <div className="avc-thread-activity">
          <Icon name="star" size={12} />
          <div><MarkdownText text={text} /></div>
        </div>
      );
    }
    if (kind === "done") {
      return (
        <div className="avc-thread-status done">
          <Icon name="check" size={13} />
          <span><MarkdownText text={text} /></span>
          {time && <time>{time}</time>}
        </div>
      );
    }
    if (kind === "error") {
      return (
        <div className="avc-thread-status error">
          <Icon name="alert" size={13} />
          <span><MarkdownText text={text} /></span>
          {time && <time>{time}</time>}
        </div>
      );
    }
    if (kind === "question") {
      return (
        <div className="avc-thread-question">
          <div><Icon name="alert" size={12} />Needs your input</div>
          <MarkdownText text={text} />
          {time && <time>{time}</time>}
        </div>
      );
    }
    if (kind === "delegation-continuation") {
      return (
        <div className="avc-thread-return">
          <div><Icon name="handover" size={12} />Delegated work returned</div>
          <MarkdownText text={text} />
          {time && <time>{time}</time>}
        </div>
      );
    }
    if (kind === "you" || role === "user") {
      return (
        <div className="avc-thread-message user">
          <div className="avc-thread-bubble"><MarkdownText text={text} /></div>
          {time && <time>{time}</time>}
        </div>
      );
    }
    return (
      <div className="avc-thread-message agent">
        {employeeAvatar(employee || { name: "AI" }, "xs")}
        <div>
          <div className="avc-thread-bubble"><MarkdownText text={text} /></div>
          {time && <time>{time}</time>}
        </div>
      </div>
    );
  }
  function DrawerMessages({ messages, employee }) {
    return (
      <div className="avc-thread-messages">
        {messages.map((message, index) => (
          <DrawerMessage key={drawerMessageKey(message, index)} message={message} employee={employee} index={index} />
        ))}
      </div>
    );
  }

  function DetailDrawer({ run, detail, children, reviewChildren, activeChildren, parent, agentsById, bridgeActivityByRunId, bridgeSpeechByRunId, busyKey, db, onClose, onSelectRun, onPrimary, onSendMessage, onRespondApproval, onStopToggle, onArchive, onDelete, onOpenThread, onTextChange }) {
    const runId = run && run.id;
    const runKey = String(runId || "").toLowerCase();
    const activityOverride = runKey && bridgeActivityByRunId && bridgeActivityByRunId[runKey];
    const speechOverride = runKey && bridgeSpeechByRunId && bridgeSpeechByRunId[runKey];
    const eventActivity = useRunBridgeActivity(runId);
    const toolEntries = useRunToolActivityStream(runId, activityOverride, db);
    if (!run) return null;
    const agent = agentsById[employeeIdForRun(run)];
    const parentAgent = parent ? agentsById[employeeIdForRun(parent)] : null;
    const pct = displayPct(run);
    const meta = statusMeta(statusOf(run));
    const activity = activityOverride || eventActivity;
    const speech = speechTextFromActivity(speechOverride || activity);
    const planSteps = planStepsFromActivity(activity);
    const primaryLabel = statusOf(run) === "needs-input" ? "Answer" : "Open thread";
    const stopped = userStopped(run);
    const canStopToggle = stopped || ["queued", "working", "needs-input", "waiting-delegates"].includes(statusOf(run));
    const messages = Array.isArray(detail && detail.messages) ? detail.messages : [];
    const approvalRequest = statusOf(run) === "done" ? null : approvalRequestFromRun(run);
    const submitDetailPrompt = () => {
      if (!String(detail && detail.text || "").trim() || busyKey) return;
      onSendMessage(run);
    };
    return (
      <>
        <div className="avc-dimmer" onClick={onClose} />
        <DrawerApprovalPanel approvalRequest={approvalRequest} messages={messages} busy={!!busyKey} onRespondApproval={onRespondApproval} />
        <aside className="avc-drawer" style={{ "--status-color": meta.color, "--status-soft": meta.soft }}>
          <header className="avc-drawer-head">
            <span className="avc-avatar-wrap large">
              {employeeAvatar(agent || { name: "AI" }, "thread")}
              <StatusBadge run={run} />
            </span>
            <div>
              <strong>{agent ? agent.name : "AI employee"}</strong>
              <em>{agent && agent.role ? agent.role : "AI employee"}</em>
              <StatusPill run={run} />
            </div>
            <button type="button" className="iconbtn" onClick={onClose} aria-label="Close details"><Icon name="close" size={14} /></button>
          </header>
          <section className="avc-drawer-title">
            <h2>{runTitle(run)}</h2>
            <div className="avc-progress big"><span style={{ width: `${pct}%` }} /></div>
            <span className="mono">{pct}%</span>
          </section>
          <div className="avc-drawer-body">
            {parent && (
              <section className="avc-lineage">
                {employeeAvatar(parentAgent || { name: "AI" }, "md")}
                <div><span>Handed off by {parentAgent ? parentAgent.name : "AI employee"}</span><strong>{handoffLabel(run)}</strong></div>
                <Icon name="handover" size={14} />
              </section>
            )}
            <DrawerChildren title={`Finished · ${reviewChildren.length}`} review rows={reviewChildren} agentsById={agentsById} onSelectRun={onSelectRun} />
            <DrawerChildren title={`In progress · ${activeChildren.length}`} rows={activeChildren} agentsById={agentsById} onSelectRun={onSelectRun} />
            <section className="avc-drawer-section">
              <header>Thread</header>
              {messages.length ? (
                <DrawerMessages messages={messages} employee={agent} />
              ) : (
                <div className="avc-thread-messages empty">
                  <p className="avc-empty-line">{approvalRequest ? "This thread has a pending request in the side panel." : detail.loading ? "Loading thread messages..." : "No messages loaded for this thread yet."}</p>
                </div>
              )}
            </section>
            <section className="avc-drawer-section">
              <header>Plan</header>
              <div className="avc-plan">
                {planSteps.length ? planSteps.map((step, index) => (
                  <div key={`${index}:${step.step}`} className={`avc-plan-row ${step.status}`}>
                    <span>{step.status === "done" ? <Icon name="check" size={10} /> : null}</span>
                    <p>{step.step}</p>
                  </div>
                )) : <p className="avc-empty-line">No live plan steps right now.</p>}
              </div>
            </section>
            <section className="avc-drawer-section">
              <header>Saying</header>
              <div className="avc-drawer-speech">{speech ? <><MarkdownText text={speech} /><b aria-hidden="true">▍</b></> : "Not speaking right now."}</div>
            </section>
            <section className="avc-drawer-section">
              <header>Tool calls</header>
              <div className="avc-drawer-tools">
                {toolEntries.length ? toolEntries.map((entry) => <ToolChip key={entry.key} activity={entry.activity} db={db} exiting={entry.exiting} />) : <p className="avc-empty-line">No active tool calls right now.</p>}
              </div>
            </section>
            {detail.loading && <p className="avc-empty-line">Loading thread details...</p>}
            {detail.error && <p className="avc-error-line">{detail.error}</p>}
          </div>
          <footer className="avc-drawer-footer">
            <textarea value={detail.text || ""} onChange={(event) => onTextChange(event.target.value)} onKeyDown={(event) => submitPromptOnEnter(event, submitDetailPrompt)} placeholder={statusOf(run) === "needs-input" ? "Answer this thread..." : stopped ? "Follow up to resume this thread..." : "Message this thread..."} />
            <div>
              <button type="button" className="btn" disabled={!!busyKey || !String(detail.text || "").trim()} onClick={() => onSendMessage(run)}><Icon name="send" size={14} />{busyKey || "Send follow-up"}</button>
              <button type="button" className="btn ghost" disabled={!!busyKey || (statusOf(run) === "needs-input" && !String(detail.text || "").trim())} onClick={() => primaryLabel === "Open thread" ? onOpenThread(run) : onPrimary(run)}><Icon name={primaryLabel === "Answer" ? "send" : "handover"} size={14} />{primaryLabel}</button>
              {onStopToggle && canStopToggle && <button type="button" className={`iconbtn ${stopped ? "" : "danger"}`} disabled={!!busyKey} onClick={() => onStopToggle(run)} title={stopped ? "Resume" : "Stop"}><Icon name={stopped ? "play" : "stop"} size={14} /></button>}
              <button type="button" className="iconbtn" disabled={!!busyKey || !run.threadId} onClick={() => onArchive(run)} title="Archive"><Icon name="archive" size={14} /></button>
              <button type="button" className="iconbtn danger" disabled={!!busyKey} onClick={() => onDelete(run)} title="Delete"><Icon name="trash" size={14} /></button>
            </div>
          </footer>
        </aside>
      </>
    );
  }

  function DrawerChildren({ title, rows, agentsById, review = false, onSelectRun }) {
    if (!rows.length) return null;
    return (
      <section className={`avc-drawer-section ${review ? "review" : ""}`}>
        <header>
          <span>{title}</span>
        </header>
        <div className="avc-drawer-child-list">
          {rows.map((run) => {
            const agent = agentsById[employeeIdForRun(run)];
            return (
              <button key={run.id} type="button" onClick={() => onSelectRun(run)}>
                {employeeAvatar(agent || { name: "AI" }, review ? "xs" : "md")}
                <span><strong>{review ? runTitle(run) : (agent && agent.name) || "AI employee"}</strong><em>{review ? ((agent && agent.name) || "AI employee") : runTitle(run)}</em></span>
                {review ? <i /> : <StatusPill run={run} />}
              </button>
            );
          })}
        </div>
      </section>
    );
  }

  function CommandCenterView({ db, query, actionCenterRoute, bridgeActivityByRunId, bridgeSpeechByRunId, onToast, onError, onNav, onNeedsInputCount, onOverviewChange, onOpenActionCenterRun }) {
    const [data, setData] = useState({ agents: [], backlog: [], runs: [], taggedTickets: [], instructions: [], needsInputCount: 0 });
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("all");
    const [itemsOpen, setItemsOpen] = useState(false);
    const [workedTicketsOpen, setWorkedTicketsOpen] = useState(false);
    const [selectedRunId, setSelectedRunId] = useState("");
    const [detail, setDetail] = useState({ runId: "", messages: [], loading: false, error: "", text: "" });
    const [busyKey, setBusyKey] = useState("");
    const [pendingAssignment, setPendingAssignment] = useState(null);
    const [selectedInstructionId, setSelectedInstructionId] = useState("");
    const [customInstructionPrompt, setCustomInstructionPrompt] = useState("");
    const [standardTaskMenu, setStandardTaskMenu] = useState(null);
    const [compose, setCompose] = useState(null);
    const [composePrompt, setComposePrompt] = useState("");
    const [archivedMenu, setArchivedMenu] = useState(null);
    const [archivedQuery, setArchivedQuery] = useState("");
    const [rosterQuery, setRosterQuery] = useState("");
    const [delegationAnimations, setDelegationAnimations] = useState({});
    const routeKeyRef = useRef("");

    const MentionTextarea = window.MentionTextarea || "textarea";
    const mentionOptions = useMemo(() => window.buildMentionOptions ? window.buildMentionOptions(db) : [], [db]);
    const agents = data.agents || [];
    const runs = useMemo(() => (data.runs || []).filter((run) => !run.archived).slice().sort(runSort), [data.runs]);
    const runsById = useMemo(() => {
      const map = {};
      runs.forEach((run) => { map[run.id] = run; });
      return map;
    }, [runs]);
    const agentsById = useMemo(() => {
      const map = {};
      agents.forEach((agent) => { map[agent.id] = agent; });
      return map;
    }, [agents]);
    const childrenByParent = useMemo(() => {
      const map = {};
      runs.forEach((run) => {
        const parentId = runParentId(run);
        if (!parentId || !runsById[parentId]) return;
        if (!map[parentId]) map[parentId] = [];
        map[parentId].push(run);
      });
      Object.keys(map).forEach((id) => map[id].sort(runSort));
      return map;
    }, [runs, runsById]);
    const rootRuns = useMemo(() => runs.filter((run) => !runParentId(run) || !runsById[runParentId(run)]).sort((a, b) => {
      const activeDiff = Number(activeRunStatus(b)) - Number(activeRunStatus(a));
      return activeDiff || runSort(a, b);
    }), [runs, runsById]);
    const threadCounts = useMemo(() => {
      const counts = {};
      runs.filter(activeRunStatus).forEach((run) => {
        const id = employeeIdForRun(run);
        counts[id] = (counts[id] || 0) + 1;
      });
      return counts;
    }, [runs]);
    const rosterAgents = useMemo(() => {
      const q = norm(rosterQuery);
      return agents
        .filter((agent) => !q || rosterSearchText(agent).includes(q))
        .slice()
        .sort((a, b) => String(a.name || "").localeCompare(String(b.name || "")));
    }, [agents, rosterQuery]);
    const selectedRun = selectedRunId ? runsById[selectedRunId] || null : null;
    const selectedChildren = selectedRun ? (childrenByParent[selectedRun.id] || []) : [];
    const selectedReviewChildren = selectedChildren.filter((run) => statusOf(run) === "review");
    const selectedActiveChildren = selectedChildren.filter((run) => statusOf(run) !== "review");
    const selectedParent = selectedRun && runParentId(selectedRun) ? runsById[runParentId(selectedRun)] : null;
    const backlogById = useMemo(() => {
      const map = {};
      (data.backlog || []).forEach((item) => { map[item.id] = item; });
      return map;
    }, [data.backlog]);
    const searchedBacklog = useMemo(() => {
      const q = norm(query);
      return (data.backlog || []).filter((item) => !q || [item.title, item.detail, item.projectName, item.customerName, item.type, item.priority].join(" ").toLowerCase().includes(q));
    }, [data.backlog, query]);
    const searchedWorkedTickets = useMemo(() => {
      const q = norm(query);
      return (data.taggedTickets || []).filter((ticket) => !q || [
        ticket.ticketTitle,
        ticket.ticketStatus,
        ticket.projectName,
        ticket.employeeName,
        ticket.runTitle,
        ticket.threadId,
        ticket.ticketNumber ? `#${ticket.ticketNumber}` : "",
      ].join(" ").toLowerCase().includes(q));
    }, [data.taggedTickets, query]);
    const workedTicketGroups = useMemo(() => {
      const order = ["In progress", "Ready for review", "Open", "Done"];
      const groups = new Map();
      searchedWorkedTickets.forEach((ticket) => {
        const status = String(ticket.ticketStatus || "Open").trim() || "Open";
        if (!groups.has(status)) groups.set(status, []);
        groups.get(status).push(ticket);
      });
      groups.forEach((tickets) => tickets.sort((a, b) =>
        String(a.projectName || "").localeCompare(String(b.projectName || ""))
        || (Number(a.ticketNumber) || 0) - (Number(b.ticketNumber) || 0)
        || String(a.ticketTitle || "").localeCompare(String(b.ticketTitle || ""))));
      return Array.from(groups.entries())
        .sort(([a], [b]) => {
          const ai = order.findIndex((item) => item.toLowerCase() === a.toLowerCase());
          const bi = order.findIndex((item) => item.toLowerCase() === b.toLowerCase());
          return (ai < 0 ? 99 : ai) - (bi < 0 ? 99 : bi) || a.localeCompare(b);
        })
        .map(([status, tickets]) => ({ status, tickets }));
    }, [searchedWorkedTickets]);
    const counts = useMemo(() => ({
      all: runs.length,
      working: runs.filter((run) => ["queued", "working", "waiting-delegates"].includes(statusOf(run))).length,
      "needs-input": runs.filter((run) => statusOf(run) === "needs-input").length,
      review: runs.filter((run) => statusOf(run) === "review").length,
      done: runs.filter((run) => statusOf(run) === "done").length,
    }), [runs]);

    const mergeRun = useCallback((run) => {
      if (!run || !run.id) return;
      setData((current) => ({
        ...current,
        runs: reconcileList(current.runs, [...(current.runs || []).filter((item) => item.id !== run.id), run].sort(runSort), (item) => item.id),
      }));
    }, []);
    const removeRun = useCallback((runId) => {
      setData((current) => ({
        ...current,
        runs: (current.runs || []).filter((run) => !sameId(run.id, runId)),
        taggedTickets: (current.taggedTickets || []).filter((ticket) => !sameId(ticket.runId, runId)),
      }));
    }, []);
    const load = useCallback(async (silent) => {
      try {
        if (!silent) setLoading(true);
        const [next] = await Promise.all([
          window.API.getActionCenter(),
          window.API.getActionCenterOffice ? window.API.getActionCenterOffice().catch(() => null) : Promise.resolve(null),
        ]);
        setData((current) => reconcileDashboard(current, next || {}));
      } catch (error) {
        if (onError) onError(error);
      } finally {
        if (!silent) setLoading(false);
      }
    }, [onError]);
    const loadWorkedTickets = useCallback(async () => {
      if (!window.API.getActionCenterTaggedTickets) return;
      try {
        const next = await window.API.getActionCenterTaggedTickets();
        setData((current) => ({
          ...current,
          taggedTickets: reconcileTaggedTickets(current.taggedTickets, Array.isArray(next) ? next : []),
        }));
      } catch (error) {
        if (onError) onError(error);
      }
    }, [onError]);
    const loadRun = useCallback(async (runId, openDetail) => {
      if (!runId || !window.API.getActionCenterRun) return null;
      try {
        if (openDetail) setDetail((current) => ({ ...current, runId, loading: true, error: "" }));
        const update = await window.API.getActionCenterRun(runId);
        setData((current) => reconcileRunUpdate(current, runId, update || {}));
        if (openDetail) setDetail((current) => ({
          ...current,
          runId,
          messages: Array.isArray(update && update.messages) ? update.messages : [],
          loading: false,
          error: update && update.exists === false ? "This thread no longer exists." : "",
        }));
        return update || null;
      } catch (error) {
        if (openDetail) setDetail((current) => ({ ...current, loading: false, error: (error && error.message) || "Failed to load thread." }));
        if (onError) onError(error);
      }
      return null;
    }, [onError]);
    const loadTransientRun = useCallback((runId) => { loadRun(runId, sameId(runId, selectedRunId)); }, [loadRun, selectedRunId]);
    const { scheduleTransientRunLoad, triggerDelegationAnimation } = useRunActivityTimers(loadTransientRun, setDelegationAnimations);
    const selectRun = useCallback((run) => {
      if (!run || !run.id) return;
      setSelectedRunId(run.id);
      setDetail({ runId: run.id, messages: [], loading: true, error: "", text: "" });
      loadRun(run.id, true);
    }, [loadRun]);

    useEffect(() => { load(false); }, [load]);
    useEffect(() => {
      if (onNeedsInputCount) onNeedsInputCount(Number(data.needsInputCount) || 0);
      if (onOverviewChange) onOverviewChange({ agents: data.agents || [], runs: data.runs || [], taggedTickets: data.taggedTickets || [], needsInputCount: Number(data.needsInputCount) || 0 });
    }, [data, onNeedsInputCount, onOverviewChange]);
    useEffect(() => {
      const routeRunId = actionCenterRoute && actionCenterRoute.runId;
      const key = `${(actionCenterRoute && actionCenterRoute.employeeId) || ""}:${routeRunId || ""}`;
      if (!key.trim() || routeKeyRef.current === key) return;
      routeKeyRef.current = key;
      if (routeRunId) {
        const run = runs.find((item) => sameId(item.id, routeRunId));
        if (run) selectRun(run);
        else loadRun(routeRunId, true).then((update) => { if (update && update.run) setSelectedRunId(update.run.id); });
      }
    }, [actionCenterRoute && actionCenterRoute.employeeId, actionCenterRoute && actionCenterRoute.runId, runs, selectRun, loadRun]);
    useEffect(() => {
      if (!window.API.subscribeToChanges) return undefined;
      const unsubscribe = window.API.subscribeToChanges((change) => {
        if (isWorkedTicketRefreshEvent(change)) {
          loadWorkedTickets();
          return;
        }
        if (!change || change.kind !== "employee-activity" || !change.jobId) return;
        const activityKind = norm(change.toolName);
        if (activityKind === "transient-activity" || activityKind === "codex-progress") {
          scheduleTransientRunLoad(change.jobId);
          return;
        }
        if (isDelegationEvent(change)) {
          const direction = delegationDirection(change);
          loadRun(change.jobId).then((update) => {
            const run = update && update.run;
            const parentId = runParentId(run);
            if (!run || !parentId) return;
            loadRun(parentId);
            triggerDelegationAnimation(parentId, run.id, direction);
          });
          return;
        }
        if (isActionCenterEvent(change) && isDurableActionCenterEvent(change)) loadRun(change.jobId, sameId(change.jobId, selectedRunId));
      });
      return () => unsubscribe();
    }, [loadRun, loadWorkedTickets, scheduleTransientRunLoad, selectedRunId, triggerDelegationAnimation]);
    useEffect(() => {
      if (!standardTaskMenu) return undefined;
      const isMenuTarget = (target) => !!(target && target.closest && target.closest(".ac-standard-task-menu"));
      const close = () => setStandardTaskMenu(null);
      const onMouseDown = (event) => { if (!isMenuTarget(event.target)) close(); };
      const onKey = (event) => { if (event.key === "Escape") close(); };
      const onScroll = (event) => { if (!isMenuTarget(event.target)) close(); };
      window.addEventListener("mousedown", onMouseDown);
      window.addEventListener("scroll", onScroll, true);
      window.addEventListener("resize", close);
      window.addEventListener("keydown", onKey);
      return () => {
        window.removeEventListener("mousedown", onMouseDown);
        window.removeEventListener("scroll", onScroll, true);
        window.removeEventListener("resize", close);
        window.removeEventListener("keydown", onKey);
      };
    }, [standardTaskMenu]);

    const instructionsFor = useCallback((item) => {
      const type = norm(item && item.type);
      const all = data.instructions || [];
      const exact = all.filter((instruction) => norm(instruction.actionItemType) === type);
      const fallback = all.filter((instruction) => norm(instruction.actionItemType) === "default");
      return (exact.length ? exact : fallback).slice().sort((a, b) => (a.position || 0) - (b.position || 0));
    }, [data.instructions]);
    const standardTasksFor = useCallback((employee) => ((employee && employee.standardTasks) || [])
      .filter((task) => task && String(task.title || "").trim() && String(task.prompt || "").trim())
      .slice()
      .sort((a, b) => (Number(a.position) || 0) - (Number(b.position) || 0)), []);
    const scopedEntities = useCallback((scopeType) => {
      const clean = norm(scopeType);
      if (clean === "project") return ((db && db.projects) || []).slice().sort((a, b) => String(a.name || "").localeCompare(String(b.name || "")));
      if (clean === "customer") return ((db && db.customers) || []).slice().sort((a, b) => String(a.name || "").localeCompare(String(b.name || "")));
      return [];
    }, [db]);
    const workPackagesForProject = useCallback((project) => ((project && project.workPackages) || [])
      .slice()
      .sort((a, b) => (Number(a.position) || 0) - (Number(b.position) || 0) || String(a.name || "").localeCompare(String(b.name || ""))), []);
    const standardTaskAllowsInput = useCallback((task) => !!(task && task.additionalInput), []);
    const standardTaskRequiresWorkPackage = useCallback((task) => norm(task && task.scopeType) === "project" && !!(task && task.workPackageScope), []);
    const standardTaskActionLabel = useCallback((task) => {
      if (task && task.scopeType) return `Select ${String(task.scopeType).toLowerCase()}`;
      return standardTaskAllowsInput(task) ? "Add custom prompt" : "Start now";
    }, [standardTaskAllowsInput]);
    const standardTaskPrompt = useCallback((task, additionalInput) => {
      const prompt = String(task && task.prompt || "").trim();
      const input = String(additionalInput || "").trim();
      return input ? `${prompt}\n\nCustom prompt:\n${input}` : prompt;
    }, []);
    const contextReferenceForStandardTask = useCallback((employee, task, entity, workPackage) => {
      const scopeType = String(task && task.scopeType || "").trim();
      const title = String(task && task.title || "Standard task").trim();
      const baseFields = {
        Employee: (employee && employee.name) || "",
        EmployeeId: (employee && employee.id) || "",
        Role: (employee && employee.role) || "AI employee",
        StandardTask: title,
      };
      if (norm(scopeType) === "project" && entity) {
        const customerId = (entity.customerIds || [])[0] || "";
        const customer = ((db && db.customers) || []).find((item) => item.id === customerId);
        if (workPackage) {
          return {
            type: "Project work package",
            id: workPackage.id || "",
            label: `${title}: ${entity.name || "Project"} / ${workPackage.name || "Work package"}`,
            url: `${currentUrlWithoutHash()}#projects/${encodeURIComponent(entity.id || "")}`,
            fields: { ...baseFields, Project: entity.name || "", ProjectId: entity.id || "", Customer: (customer && customer.name) || "", CustomerId: customerId, WorkPackage: workPackage.name || "", WorkPackageTitle: workPackage.name || "", WorkPackageId: workPackage.id || "" },
          };
        }
        return {
          type: "Project",
          id: entity.id || "",
          label: `${title}: ${entity.name || "Project"}`,
          url: `${currentUrlWithoutHash()}#projects/${encodeURIComponent(entity.id || "")}`,
          fields: { ...baseFields, Project: entity.name || "", ProjectId: entity.id || "", Customer: (customer && customer.name) || "", CustomerId: customerId },
        };
      }
      if (norm(scopeType) === "customer" && entity) {
        return {
          type: "Customer",
          id: entity.id || "",
          label: `${title}: ${entity.name || "Customer"}`,
          url: currentUrlWithoutHash(),
          fields: { ...baseFields, Customer: entity.name || "", CustomerId: entity.id || "" },
        };
      }
      return {
        ...activityReferenceFor(employee),
        label: title,
        fields: baseFields,
      };
    }, [db]);
    const assign = useCallback((itemId, employeeId) => {
      const item = backlogById[itemId];
      const options = instructionsFor(item);
      setPendingAssignment({ itemId, employeeId });
      setSelectedInstructionId(options[0] ? options[0].id : CUSTOM_INSTRUCTION_ID);
      setCustomInstructionPrompt("");
    }, [backlogById, instructionsFor]);
    const confirmAssignment = useCallback(async () => {
      const useCustom = selectedInstructionId === CUSTOM_INSTRUCTION_ID;
      const customPrompt = customInstructionPrompt.trim();
      if (!pendingAssignment || (useCustom ? !customPrompt : !selectedInstructionId) || busyKey) return;
      const { itemId, employeeId } = pendingAssignment;
      try {
        setBusyKey("Assigning...");
        const run = await window.API.assignActionCenterRun(itemId, employeeId, useCustom ? "" : selectedInstructionId, useCustom ? customPrompt : "");
        setData((current) => ({
          ...current,
          backlog: (current.backlog || []).filter((item) => item.id !== itemId),
          runs: reconcileList(current.runs, [...(current.runs || []).filter((item) => item.id !== run.id), run].sort(runSort), (item) => item.id),
        }));
        setPendingAssignment(null);
        selectRun(run);
        if (onToast) onToast("Action item assigned");
      } catch (error) {
        if (onError) onError(error);
      } finally {
        setBusyKey("");
      }
    }, [busyKey, customInstructionPrompt, pendingAssignment, selectRun, selectedInstructionId, onError, onToast]);
    const submitStartComposer = useCallback(async () => {
      const clean = composePrompt.trim();
      if (!compose || !clean || busyKey) return;
      try {
        setBusyKey("Sending...");
        const run = await window.API.startActionCenterContextTask(compose.employee.id, clean, compose.reference);
        setCompose(null);
        setComposePrompt("");
        await load(true);
        if (run && run.id) selectRun(run);
        if (onToast) onToast("Action Center task queued");
      } catch (error) {
        if (onError) onError(error);
      } finally {
        setBusyKey("");
      }
    }, [busyKey, compose, composePrompt, load, onError, onToast, selectRun]);
    const openComposer = useCallback((employee) => {
      setCompose({ employee, reference: activityReferenceFor(employee) });
      setComposePrompt("");
    }, []);
    const openStandardTaskMenu = useCallback((employee, event) => {
      const tasks = standardTasksFor(employee);
      if (!employee || tasks.length === 0) {
        openComposer(employee);
        return;
      }
      const rect = event && event.currentTarget && event.currentTarget.getBoundingClientRect ? event.currentTarget.getBoundingClientRect() : null;
      const width = 420;
      const margin = 10;
      const maxLeft = Math.max(margin, window.innerWidth - width - margin);
      const maxTop = Math.max(margin, window.innerHeight - margin - 560);
      setArchivedMenu(null);
      setStandardTaskMenu({
        employee,
        mode: "tasks",
        task: null,
        x: rect ? Math.min(Math.max(margin, rect.left - width + rect.width), maxLeft) : margin,
        y: rect ? Math.min(Math.max(margin, rect.bottom + 8), maxTop) : margin,
      });
    }, [openComposer, standardTasksFor]);
    const submitStandardTask = useCallback(async (employee, task, entity, options = {}) => {
      const prompt = standardTaskPrompt(task, options.additionalInput);
      if (!employee || !prompt || busyKey) return;
      const reference = contextReferenceForStandardTask(employee, task, entity, options.workPackage);
      try {
        setBusyKey("Starting...");
        const run = await window.API.startActionCenterContextTask(employee.id, prompt, reference);
        setStandardTaskMenu(null);
        await load(true);
        if (run && run.id) selectRun(run);
        if (onToast) onToast("Action Center task queued");
      } catch (error) {
        if (onError) onError(error);
      } finally {
        setBusyKey("");
      }
    }, [busyKey, contextReferenceForStandardTask, load, onError, onToast, selectRun, standardTaskPrompt]);
    const openArchivedThreadsMenu = useCallback((employee, event) => {
      if (!employee) return;
      const rect = event && event.currentTarget && event.currentTarget.getBoundingClientRect ? event.currentTarget.getBoundingClientRect() : null;
      const nextMenu = { employee, x: rect ? rect.left : 70, y: rect ? rect.bottom + 8 : 70, loading: true, error: "", threads: [] };
      setStandardTaskMenu(null);
      setArchivedQuery("");
      setArchivedMenu(nextMenu);
      if (!window.API.listArchivedActionCenterThreads) {
        setArchivedMenu({ ...nextMenu, loading: false, error: "Archived thread endpoint is not available." });
        return;
      }
      window.API.listArchivedActionCenterThreads(employee.id)
        .then((threads) => setArchivedMenu((current) => current && sameId(current.employee.id, employee.id) ? { ...current, loading: false, threads: Array.isArray(threads) ? threads : [] } : current))
        .catch((error) => {
          setArchivedMenu((current) => current && sameId(current.employee.id, employee.id) ? { ...current, loading: false, error: (error && error.message) || "Failed to load archived threads.", threads: [] } : current);
          if (onError) onError(error);
        });
    }, [onError]);
    const archivedThreads = useMemo(() => {
      const q = norm(archivedQuery);
      const threads = archivedMenu && Array.isArray(archivedMenu.threads) ? archivedMenu.threads : [];
      return q ? threads.filter((thread) => [thread.title, thread.preview, thread.detail, thread.projectName, thread.customerName, thread.contextScopeLabel, thread.threadId].join(" ").toLowerCase().includes(q)) : threads;
    }, [archivedMenu, archivedQuery]);

    const primaryDetailAction = useCallback(async (run) => {
      if (!run || busyKey) return;
      try {
        setBusyKey("Answering...");
        const text = String(detail.text || "").trim();
        const next = await window.API.answerActionCenterRun(run.id, text);
        mergeRun(next || run);
        setDetail((current) => ({ ...current, text: "" }));
        await loadRun(run.id, true);
        if (onToast) onToast("Answer sent");
      } catch (error) {
        if (onError) onError(error);
      } finally {
        setBusyKey("");
      }
    }, [busyKey, detail.text, loadRun, mergeRun, onError, onToast]);
    const sendDetailMessage = useCallback(async (run) => {
      const text = String(detail.text || "").trim();
      if (!run || !text || busyKey) return;
      try {
        setBusyKey("Sending...");
        const next = await window.API.sendActionCenterRunMessage(run.id, text);
        mergeRun(next || run);
        setDetail((current) => ({ ...current, text: "" }));
        await loadRun(run.id, true);
        if (onToast) onToast("Message sent");
      } catch (error) {
        if (onError) onError(error);
      } finally {
        setBusyKey("");
      }
    }, [busyKey, detail.text, loadRun, mergeRun, onError, onToast]);
    const respondRunApproval = useCallback(async (run, requestId, decision) => {
      const jobId = approvalJobIdForRun(run);
      if (!run || !jobId || !requestId || !window.API.respondCodexPromptApproval || busyKey) {
        setDetail((current) => ({ ...current, error: "Approval endpoint is not available." }));
        return;
      }
      try {
        setBusyKey("Responding...");
        setDetail((current) => ({ ...current, error: "" }));
        await window.API.respondCodexPromptApproval(jobId, requestId, decision);
        await loadRun(run.id, true);
        await load(true);
        if (onToast) onToast(structuredQuestionDecision(decision) ? "Answers submitted" : approvalToastForDecision(decision));
      } catch (error) {
        setDetail((current) => ({ ...current, error: (error && error.message) || "Request failed." }));
        if (onError) onError(error);
      } finally {
        setBusyKey("");
      }
    }, [busyKey, load, loadRun, onError, onToast]);
    const respondDrawerApproval = useCallback(async (requestId, decision) => {
      await respondRunApproval(selectedRun, requestId, decision);
    }, [respondRunApproval, selectedRun]);
    const archiveRun = useCallback(async (run) => {
      if (!run || !window.API.setActionCenterRunArchived || busyKey) return;
      try {
        setBusyKey("Archiving...");
        await window.API.setActionCenterRunArchived(run.id, true);
        removeRun(run.id);
        setSelectedRunId("");
        if (onToast) onToast("Thread archived");
      } catch (error) {
        if (onError) onError(error);
      } finally {
        setBusyKey("");
      }
    }, [busyKey, onError, onToast, removeRun]);
    const deleteRun = useCallback(async (run) => {
      if (!run || !window.API.deleteActionCenterRun || busyKey) return;
      if (!window.confirm("Delete this thread? This removes it from the Action Center and queues Codex deletion when available.")) return;
      try {
        setBusyKey("Deleting...");
        await window.API.deleteActionCenterRun(run.id);
        removeRun(run.id);
        setSelectedRunId("");
        if (onToast) onToast("Thread deleted");
      } catch (error) {
        if (onError) onError(error);
      } finally {
        setBusyKey("");
      }
    }, [busyKey, onError, onToast, removeRun]);
    const stopOrResumeRun = useCallback(async (run) => {
      if (!run || !window.API.stopActionCenterRun || busyKey) return;
      const stopped = userStopped(run);
      if (!stopped && !["queued", "working", "needs-input", "waiting-delegates"].includes(statusOf(run))) return;
      try {
        setBusyKey(stopped ? "Resuming..." : "Stopping...");
        const result = await window.API.stopActionCenterRun(run.id);
        await loadRun(run.id, true);
        await load(true);
        if (onToast) onToast(result && result.resumed ? "Thread resumed" : "Thread stopped");
      } catch (error) {
        if (onError) onError(error);
      } finally {
        setBusyKey("");
      }
    }, [busyKey, load, loadRun, onError, onToast]);

    if (loading) return <div className="av-shell av-command"><div className="ac-loading">Loading Action Center...</div></div>;
    if (!agents.length) {
      return (
        <div className="av-shell av-command">
          <div className="ac-empty-full">
            <Icon name="server" size={30} />
            <strong>No AI employees on shift</strong>
            <p>Create an active AI employee before assigning Action Center work.</p>
            <button className="btn" type="button" onClick={() => onNav && onNav("employees")}><Icon name="employees" size={14} />Open employees</button>
          </div>
        </div>
      );
    }

    const pendingItem = pendingAssignment ? backlogById[pendingAssignment.itemId] : null;
    const pendingAgent = pendingAssignment ? agents.find((agent) => agent.id === pendingAssignment.employeeId) : null;
    const pendingInstructions = pendingItem ? instructionsFor(pendingItem) : [];
    const customInstructionSelected = selectedInstructionId === CUSTOM_INSTRUCTION_ID;
    const chosenInstruction = customInstructionSelected ? null : (pendingInstructions.find((instruction) => instruction.id === selectedInstructionId) || pendingInstructions[0] || null);
    const canConfirmAssignment = customInstructionSelected ? !!customInstructionPrompt.trim() : !!chosenInstruction;

    return (
      <div className="av-shell av-command">
        <header className="avc-toolbar">
          <div className="avc-live-pill"><i />{counts.working} threads live</div>
          <div className="av-filters" role="group" aria-label="Filter runs">
            {[
              ["all", "All"],
              ["working", "Working"],
              ["needs-input", "Needs you"],
              ["review", "Finished"],
              ["done", "Done"],
            ].map(([key, label]) => (
              <button key={key} type="button" className={`av-filter ${filter === key ? "on" : ""} ${statusMeta(key).cls}`} onClick={() => setFilter(key)}>
                <span className="av-filter-dot" />
                <span>{label}</span>
                <em>{counts[key]}</em>
              </button>
            ))}
          </div>
          <button type="button" className={`av-items-header ${itemsOpen ? "on" : ""}`} onClick={() => setItemsOpen((open) => !open)}>
            <Icon name="flag" size={14} />
            <span>Action items</span>
            <em>{searchedBacklog.length}</em>
          </button>
          <button type="button" className={`av-items-header ${workedTicketsOpen ? "on" : ""}`} onClick={() => setWorkedTicketsOpen((open) => !open)}>
            <Icon name="list" size={14} />
            <span>Worked tickets</span>
            <em>{searchedWorkedTickets.length}</em>
          </button>
        </header>
        <div className="avc-body">
          <aside className="avc-roster-rail">
            <header>
              <span>Roster</span>
              <em>{rosterAgents.length}/{agents.length}</em>
            </header>
            <div className="avc-roster-filter">
              <Icon name="search" size={13} />
              <input type="search" value={rosterQuery} onChange={(event) => setRosterQuery(event.target.value)} placeholder="Filter roster" aria-label="Filter roster by description, role, skill, or plugin" />
            </div>
            <div className="avc-roster-list">
              {rosterAgents.map((agent) => {
                const activeCount = threadCounts[agent.id] || 0;
                const description = String(agent.description || "").trim();
                return (
                  <div key={agent.id} className="avc-roster-row">
                    <button type="button" onClick={(event) => openStandardTaskMenu(agent, event)} title={`Start thread with ${agent.name}`}>
                      {employeeAvatar(agent, "xs")}
                      <span className="avc-roster-copy">
                        <strong>{agent.name}</strong>
                        {!!description && <small>{description}</small>}
                      </span>
                      {activeCount > 0 && <em className="mono">x{activeCount}</em>}
                    </button>
                    <button type="button" title={`Archived threads for ${agent.name}`} onClick={(event) => openArchivedThreadsMenu(agent, event)}><Icon name="archive" size={12} /></button>
                  </div>
                );
              })}
              {rosterAgents.length === 0 && <div className="avc-roster-empty">No employees match this filter.</div>}
            </div>
          </aside>
          <main className="avc-canvas" onClick={(event) => { if (event.target === event.currentTarget) setSelectedRunId(""); }}>
            {rootRuns.length ? (
              <div className="avc-forest">
                {rootRuns.map((run) => (
                  <TreeNode
                    key={run.id}
                    run={run}
                    childrenByParent={childrenByParent}
                    agentsById={agentsById}
                    runsById={runsById}
                    threadCounts={threadCounts}
                    selectedRunId={selectedRunId}
                    filter={filter}
                    delegationAnimations={delegationAnimations}
                    busyKey={busyKey}
                    bridgeActivityByRunId={bridgeActivityByRunId}
                    bridgeSpeechByRunId={bridgeSpeechByRunId}
                    onSelectRun={selectRun}
                    onRespondApproval={respondRunApproval}
                    db={db}
                  />
                ))}
              </div>
            ) : (
              <div className="avc-empty-state">
                <Icon name="handover" size={28} />
                <strong>No active threads</strong>
                <p>Start a thread from the idle roster or assign an action item.</p>
              </div>
            )}
          </main>
          {itemsOpen && (
            <aside className="avc-items-panel">
              <header><div><strong>Action items</strong><span>{searchedBacklog.length} unassigned · assign to anyone</span></div><button type="button" className="iconbtn" onClick={() => setItemsOpen(false)}><Icon name="close" size={14} /></button></header>
              <div className="av-items-list">
                {searchedBacklog.length === 0 && <div className="ac-empty">Everything is assigned.</div>}
                {searchedBacklog.map((item) => {
                  const meta = sourceMeta(item.type);
                  const suggested = agents.find((agent) => agent.id === item.suggestedEmployeeId) || agents[0];
                  return (
                    <article key={item.id} className="av-action-card">
                      <div className="av-action-source">
                        <span><Icon name={meta.icon} size={13} /></span>
                        <em>{meta.label}</em>
                        <strong className={priorityClass(item.priority)}>{priorityLabel(item.priority)}</strong>
                      </div>
                      <h3>{item.title || "Untitled action item"}</h3>
                      <p>{item.projectName || item.customerName || meta.label}</p>
                      {suggested && (
                        <button className="av-suggest" type="button" disabled={!!busyKey} onClick={() => assign(item.id, suggested.id)}>
                          <span>Assign</span>
                          {employeeAvatar(suggested, "xs")}
                          <strong>{firstName(suggested.name)}</strong>
                        </button>
                      )}
                    </article>
                  );
                })}
              </div>
            </aside>
          )}
          {workedTicketsOpen && (
            <aside className="avc-items-panel avc-worked-ticket-panel">
              <header><div><strong>Worked tickets</strong><span>{searchedWorkedTickets.length} with an active thread reference</span></div><button type="button" className="iconbtn" onClick={() => setWorkedTicketsOpen(false)}><Icon name="close" size={14} /></button></header>
              <div className="av-items-list">
                {workedTicketGroups.length === 0 && <div className="ac-empty">No worked tickets yet.</div>}
                {workedTicketGroups.map((group) => (
                  <section key={group.status} className="av-worked-ticket-group">
                    <h3><span>{group.status}</span><em>{group.tickets.length}</em></h3>
                    {group.tickets.map((ticket) => (
                      <button
                        key={`${ticket.projectId}:${ticket.ticketId}`}
                        type="button"
                        className="av-tagged-ticket"
                        onClick={() => {
                          setWorkedTicketsOpen(false);
                          const run = runsById[ticket.runId];
                          if (run) selectRun(run);
                          else loadRun(ticket.runId, true).then((update) => { if (update && update.run) setSelectedRunId(update.run.id); });
                        }}
                      >
                        <span className="av-ticket-number">{Number(ticket.ticketNumber) > 0 ? `#${ticket.ticketNumber}` : "Ticket"}</span>
                        <span>
                          <strong>{ticket.ticketTitle || "Untitled ticket"}</strong>
                          <em>{ticket.projectName || "Project"} · {ticket.employeeName || "AI employee"}</em>
                        </span>
                        <small>{ticket.runStatus || "thread"}</small>
                      </button>
                    ))}
                  </section>
                ))}
              </div>
            </aside>
          )}
        </div>
        <DetailDrawer
          run={selectedRun}
          detail={detail}
          children={selectedChildren}
          reviewChildren={selectedReviewChildren}
          activeChildren={selectedActiveChildren}
          parent={selectedParent}
          agentsById={agentsById}
          bridgeActivityByRunId={bridgeActivityByRunId}
          bridgeSpeechByRunId={bridgeSpeechByRunId}
          busyKey={busyKey}
          db={db}
          onClose={() => setSelectedRunId("")}
          onSelectRun={selectRun}
          onPrimary={primaryDetailAction}
          onSendMessage={sendDetailMessage}
          onRespondApproval={respondDrawerApproval}
          onStopToggle={stopOrResumeRun}
          onArchive={archiveRun}
          onDelete={deleteRun}
          onOpenThread={(run) => onOpenActionCenterRun && onOpenActionCenterRun(employeeIdForRun(run), run.id)}
          onTextChange={(text) => setDetail((current) => ({ ...current, text }))}
        />
        {standardTaskMenu && (
          <div className="ac-standard-task-menu" style={{ left: standardTaskMenu.x, top: standardTaskMenu.y }} onMouseDown={(event) => event.stopPropagation()}>
            {standardTaskMenu.mode === "tasks" ? (
              <div className="ac-standard-task-list">
                {standardTasksFor(standardTaskMenu.employee).map((task) => (
                  <button
                    key={task.id || task.title}
                    type="button"
                    className="ac-standard-task"
                    disabled={!!busyKey}
                    onClick={() => {
                      if (task.scopeType) setStandardTaskMenu({ ...standardTaskMenu, mode: "scope", task });
                      else if (standardTaskAllowsInput(task)) setStandardTaskMenu({ ...standardTaskMenu, mode: "input", task, entity: null, workPackage: null, input: "" });
                      else submitStandardTask(standardTaskMenu.employee, task, null);
                    }}
                  >
                    <span><strong>{task.title}</strong><em>{standardTaskActionLabel(task)}</em></span>
                    <Icon name={task.scopeType || standardTaskAllowsInput(task) ? "chevR" : "send"} size={13} />
                  </button>
                ))}
                <button type="button" className="ac-standard-task custom" onClick={() => { setStandardTaskMenu(null); openComposer(standardTaskMenu.employee); }}>
                  <span><strong>Custom prompt</strong><em>Write a one-off task</em></span>
                  <Icon name="edit" size={13} />
                </button>
              </div>
            ) : standardTaskMenu.mode === "scope" ? (
              <div className="ac-standard-task-list scoped">
                <button type="button" className="ac-standard-task back" onClick={() => setStandardTaskMenu({ ...standardTaskMenu, mode: "tasks", task: null })}>
                  <Icon name="chevL" size={13} />
                  <span><strong>Back</strong><em>{standardTaskMenu.task.title}</em></span>
                </button>
                {scopedEntities(standardTaskMenu.task.scopeType).map((entity) => (
                  <button
                    key={entity.id}
                    type="button"
                    className="ac-standard-task"
                    disabled={!!busyKey}
                    onClick={() => {
                      if (standardTaskRequiresWorkPackage(standardTaskMenu.task)) setStandardTaskMenu({ ...standardTaskMenu, mode: "workPackage", entity });
                      else if (standardTaskAllowsInput(standardTaskMenu.task)) setStandardTaskMenu({ ...standardTaskMenu, mode: "input", entity, workPackage: null, input: "" });
                      else submitStandardTask(standardTaskMenu.employee, standardTaskMenu.task, entity);
                    }}
                  >
                    <span><strong>{entity.name || "Untitled"}</strong><em>{standardTaskMenu.task.title}</em></span>
                    <Icon name={standardTaskRequiresWorkPackage(standardTaskMenu.task) || standardTaskAllowsInput(standardTaskMenu.task) ? "chevR" : "send"} size={13} />
                  </button>
                ))}
                {scopedEntities(standardTaskMenu.task.scopeType).length === 0 && <div className="ac-empty">No {standardTaskMenu.task.scopeType.toLowerCase()}s available.</div>}
              </div>
            ) : standardTaskMenu.mode === "workPackage" ? (
              <div className="ac-standard-task-list scoped">
                <button type="button" className="ac-standard-task back" onClick={() => setStandardTaskMenu({ ...standardTaskMenu, mode: "scope", entity: null, workPackage: null })}>
                  <Icon name="chevL" size={13} />
                  <span><strong>Back</strong><em>{standardTaskMenu.entity && standardTaskMenu.entity.name}</em></span>
                </button>
                {workPackagesForProject(standardTaskMenu.entity).map((workPackage) => (
                  <button
                    key={workPackage.id}
                    type="button"
                    className="ac-standard-task"
                    disabled={!!busyKey}
                    onClick={() => {
                      if (standardTaskAllowsInput(standardTaskMenu.task)) setStandardTaskMenu({ ...standardTaskMenu, mode: "input", workPackage, input: "" });
                      else submitStandardTask(standardTaskMenu.employee, standardTaskMenu.task, standardTaskMenu.entity, { workPackage });
                    }}
                  >
                    <span><strong>{workPackage.name || "Work package"}</strong><em>{standardTaskMenu.entity && standardTaskMenu.entity.name}</em></span>
                    <Icon name={standardTaskAllowsInput(standardTaskMenu.task) ? "chevR" : "send"} size={13} />
                  </button>
                ))}
                {workPackagesForProject(standardTaskMenu.entity).length === 0 && <div className="ac-empty">No work packages available.</div>}
              </div>
            ) : (
              <form className="ac-standard-task-input" onSubmit={(event) => { event.preventDefault(); submitStandardTask(standardTaskMenu.employee, standardTaskMenu.task, standardTaskMenu.entity, { workPackage: standardTaskMenu.workPackage, additionalInput: standardTaskMenu.input }); }}>
                <button
                  type="button"
                  className="ac-standard-task back"
                  onClick={() => setStandardTaskMenu({
                    ...standardTaskMenu,
                    mode: standardTaskMenu.workPackage ? "workPackage" : standardTaskMenu.task && standardTaskMenu.task.scopeType ? "scope" : "tasks",
                    input: "",
                  })}
                >
                  <Icon name="chevL" size={13} />
                  <span><strong>Back</strong><em>{standardTaskMenu.task.title}</em></span>
                </button>
                <label>
                  <span>Custom prompt</span>
                  <MentionTextarea
                    className="inp"
                    rows={10}
                    value={standardTaskMenu.input || ""}
                    mentionOptions={mentionOptions}
                    onValueChange={(nextValue) => setStandardTaskMenu({ ...standardTaskMenu, input: nextValue })}
                    onChange={(event) => setStandardTaskMenu({ ...standardTaskMenu, input: event.target.value })}
                    onKeyDown={(event) => submitPromptOnEnter(event, () => submitStandardTask(standardTaskMenu.employee, standardTaskMenu.task, standardTaskMenu.entity, { workPackage: standardTaskMenu.workPackage, additionalInput: standardTaskMenu.input }))}
                    placeholder="Add optional instructions for this run..."
                    autoFocus
                    data-appligator-id="standard-task-input-mentions"
                  />
                </label>
                <button type="submit" className="btn sm primary" disabled={!!busyKey}><Icon name="send" size={13} />Start task</button>
              </form>
            )}
          </div>
        )}
        {archivedMenu && (
          <div className="ac-archived-thread-menu" style={{ left: archivedMenu.x, top: archivedMenu.y }} onMouseDown={(event) => event.stopPropagation()}>
            <header>
              <div><strong>Archived threads</strong><span>{archivedMenu.employee ? archivedMenu.employee.name : "AI employee"}</span></div>
              <button type="button" className="iconbtn" title="Close" onClick={() => setArchivedMenu(null)}><Icon name="close" size={14} /></button>
            </header>
            <label className="ac-archived-search"><Icon name="search" size={13} /><input value={archivedQuery} onChange={(event) => setArchivedQuery(event.target.value)} placeholder="Search archived threads..." autoFocus /></label>
            <div className="ac-archived-thread-list">
              {archivedMenu.loading && <div className="ac-empty">Loading archived threads...</div>}
              {!archivedMenu.loading && archivedMenu.error && <div className="ac-empty">{archivedMenu.error}</div>}
              {!archivedMenu.loading && !archivedMenu.error && archivedThreads.map((thread) => (
                <button key={thread.threadId} type="button" className="ac-archived-thread" disabled={!thread.runId} onClick={() => {
                  if (!thread.runId) return;
                  setArchivedMenu(null);
                  setSelectedRunId(thread.runId);
                  loadRun(thread.runId, true);
                }}>
                  <span><strong>{thread.title || thread.preview || thread.contextScopeLabel || "Archived thread"}</strong><em>{[thread.projectName || thread.customerName || thread.contextScopeLabel, dateTimeLabel(thread.updatedAt)].filter(Boolean).join(" - ") || thread.threadId}</em></span>
                  <Icon name={thread.runId ? "chevR" : "unlink"} size={13} />
                </button>
              ))}
              {!archivedMenu.loading && !archivedMenu.error && archivedThreads.length === 0 && <div className="ac-empty">{archivedQuery.trim() ? "No archived threads match this search." : "No archived threads."}</div>}
            </div>
          </div>
        )}
        {pendingAssignment && (
          <div className="ac-modal-overlay" onMouseDown={(event) => { if (event.target === event.currentTarget) setPendingAssignment(null); }}>
            <section className="ac-assign-modal" role="dialog" aria-modal="true" aria-labelledby="ac-assign-title">
              <header className="ac-assign-head">
                <div><h2 id="ac-assign-title">Assign task</h2><p>{pendingItem ? pendingItem.title : "Action item"} · {pendingAgent ? pendingAgent.name : "AI employee"}</p></div>
                <button className="iconbtn" type="button" onClick={() => setPendingAssignment(null)} aria-label="Close"><Icon name="close" size={16} /></button>
              </header>
              <div className="ac-instruction-list">
                {pendingInstructions.map((instruction) => (
                  <button key={instruction.id} type="button" className={`ac-instruction-option ${instruction.id === (chosenInstruction && chosenInstruction.id) ? "selected" : ""}`} onClick={() => setSelectedInstructionId(instruction.id)}>
                    <span className="ac-instruction-radio" />
                    <span><strong>{instruction.title}</strong><em>{instruction.description}</em></span>
                  </button>
                ))}
                <button type="button" className={`ac-instruction-option ${customInstructionSelected ? "selected" : ""}`} onClick={() => setSelectedInstructionId(CUSTOM_INSTRUCTION_ID)}>
                  <span className="ac-instruction-radio" />
                  <span><strong>Custom command</strong><em>Write a one-off prompt for this assignment.</em></span>
                </button>
              </div>
              {customInstructionSelected && <div className="ac-custom-command"><label htmlFor="ac-custom-command">Custom prompt</label><textarea id="ac-custom-command" rows="6" value={customInstructionPrompt} onChange={(event) => setCustomInstructionPrompt(event.target.value)} /></div>}
              {chosenInstruction && <div className="ac-instruction-prompt"><strong>Prompt behind this instruction</strong><p>{chosenInstruction.prompt}</p></div>}
              <footer className="ac-assign-actions"><button className="btn ghost" type="button" onClick={() => setPendingAssignment(null)}>Cancel</button><button className="btn" type="button" disabled={!canConfirmAssignment || !!busyKey} onClick={confirmAssignment}><Icon name="handover" size={14} />Assign</button></footer>
            </section>
          </div>
        )}
        {compose && (
          <div className="ac-modal-overlay" onMouseDown={(event) => { if (event.target === event.currentTarget && !busyKey) setCompose(null); }}>
            <section className="ai-compose-modal ac-start-composer" role="dialog" aria-modal="true" aria-labelledby="av-compose-title">
              <header>
                {employeeAvatar(compose.employee, "md")}
                <div><h2 id="av-compose-title">Message {compose.employee.name}</h2><p>{compose.reference.type} · {compose.reference.label}</p></div>
                <button className="icon-btn" type="button" title="Close" onClick={() => !busyKey && setCompose(null)} disabled={!!busyKey}><Icon name="close" size={16} /></button>
              </header>
              <div className="body">
                <div className="ai-compose-reference"><span>{compose.reference.type}</span><strong>{compose.reference.label}</strong></div>
                <div className="field">
                  <label>Prompt</label>
                  <MentionTextarea
                    className="inp ai-compose-textarea"
                    rows={10}
                    value={composePrompt}
                    mentionOptions={mentionOptions}
                    onValueChange={setComposePrompt}
                    onChange={(event) => setComposePrompt(event.target.value)}
                    onKeyDown={(event) => submitPromptOnEnter(event, submitStartComposer)}
                    placeholder="What should this employee do?"
                    autoFocus
                    data-appligator-id="action-center-start-composer-mentions"
                  />
                </div>
              </div>
              <footer><button className="btn ghost" type="button" onClick={() => setCompose(null)} disabled={!!busyKey}>Cancel</button><button className="btn" type="button" onClick={submitStartComposer} disabled={!!busyKey || !composePrompt.trim()}><Icon name="send" size={14} />{busyKey || "Send"}</button></footer>
            </section>
          </div>
        )}
      </div>
    );
  }

  window.ActivityView = CommandCenterView;
  window.ActionCenter = React.memo(CommandCenterView);
})();
